import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';

import { truncarRotuloCategoria, tituloTooltipCompleto } from '../grafico-rotulos';
import { EMPTY, Observable, catchError, expand, finalize, forkJoin, map, of, reduce } from 'rxjs';

import { ComissaoAtividadeFiltroRequest, ComissaoSituacaoAtividadeResponse } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { ComissaoAtividadeService } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.service';
import {
    ComissaoEstatisticaAgrupadaResponse,
    ComissaoEstatisticaAnaliticoPageResponse,
    ComissaoEstatisticaAnaliticoResponse,
    ComissaoEstatisticaConfirmacaoResponse,
    ComissaoEstatisticaFunilResponse
} from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { ComissaoEstatisticaService } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.service';
import {
    ComissaoLocalReuniaoResponse,
    ComissaoReuniaoDashboardSummaryResponse,
    ComissaoReuniaoFiltroRequest,
    EComissaoReuniaoMarcacaoAtaFiltro,
    EComissaoReuniaoTipoConvocacaoFiltro,
    EComissaoReuniaoTipoPeriodoFiltro,
    ComissaoStatusAtaResponse,
    ComissaoTipoComissaoResponse
} from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePicker } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { type PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';

import { AuthService } from '@/core/auth/auth.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { resolverCorSemantica } from './chart-cores-semanticas';
import { Razao } from './razao';
import { DetalhamentoPendencias } from '../../gestao-administrativa/detalhamento-pendencias/detalhamento-pendencias';
import { ConselheirosEstatisticasComponent } from './conselheiros/conselheiros-estatisticas';
import { PainelComissoesContainer } from '../painel-comissoes/painel-comissoes';
import { IndicadorPendencia } from '../../gestao-administrativa/mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../../gestao-administrativa/mapa-pendencias/pendencia-linha.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface IndicadorComissao {
    titulo: string;
    valor: string;
    legenda: string;
    icon: string;
    classe: string;
    secao: string;
    analiticoTipo?: AnaliticoGraficoComissaoId;
    analiticoGrupo?: string;
}

interface ItemFunilComissao {
    label: string;
    total: number;
    percentual: number;
}

interface ConfirmacaoReuniao {
    label: string;
    totalConvocacoes: number;
    totalConfirmacoes: number;
    percentual: number;
}

interface OpcaoFiltro<T> {
    label: string;
    value: T;
}

interface SecaoDashboard {
    id: string;
    label: string;
    icon: string;
    descricao: string;
}

type AnaliticoGraficoComissaoId =
    | 'funil'
    | 'reunioes-status'
    | 'reunioes-evolucao'
    | 'reunioes-tipo-comissao'
    | 'atas-status'
    | 'atas-protocolo'
    | 'modalidade'
    | 'reunioes-atos-comissao'
    | 'reunioes-atos-distribuicao'
    | 'quorum-medio-comissao'
    | 'quorum-faixa'
    | 'reunioes-duracao-faixa'
    | 'atas-lead-time-faixa'
    | 'reunioes-local'
    | 'reunioes-portal'
    | 'atas-importancia'
    | 'agenda-semanal'
    | 'atas-producao-mensal'
    | 'atividades-evolucao'
    | 'pendencias-convocacao'
    | 'atividades-situacao'
    | 'atividades-responsavel'
    | 'atividades-prazo'
    | 'confirmacoes'
    | 'membros-lgpd'
    | 'membros-quitacao'
    | 'membros-participacao'
    | 'membros-multivinculo'
    | 'membros-status'
    | 'membros-uf'
    | 'convocacoes-presencas'
    | 'convocacoes-emails'
    | 'convocacoes-meio'
    | 'convocacoes-sms-status'
    | 'convocacoes-convidados'
    | 'comissoes-tipo'
    | 'comissoes-status'
    | 'comissoes-sem-reuniao'
    | 'comissoes-extintas-ano'
    | 'comissoes-entidades-externas'
    | 'comissoes-faixa-membros'
    | 'atividades-comissao'
    | 'atividades-tempo-conclusao';

interface AnaliticoGraficoComissao {
    indicador: IndicadorPendencia;
    colunaUf: string;
    colunaIdentificador: string;
    colunaNome: string;
    colunaSituacao: string;
    colunaDetalhe: string | null;
}

/** Acumulador com todos os conjuntos de dados do dashboard, preenchido por seção sob demanda. */
interface ResultadoDashboard {
    resumo: ComissaoReuniaoDashboardSummaryResponse | null;
    reunioesStatus: ComissaoEstatisticaAgrupadaResponse[];
    reunioesEvolucao: ComissaoEstatisticaAgrupadaResponse[];
    reunioesTipoComissao: ComissaoEstatisticaAgrupadaResponse[];
    atasStatus: ComissaoEstatisticaAgrupadaResponse[];
    atasProtocolo: ComissaoEstatisticaAgrupadaResponse[];
    modalidade: ComissaoEstatisticaAgrupadaResponse[];
    atosComissao: ComissaoEstatisticaAgrupadaResponse[];
    atosDistribuicao: ComissaoEstatisticaAgrupadaResponse[];
    quorumMedioComissao: ComissaoEstatisticaAgrupadaResponse[];
    quorumFaixa: ComissaoEstatisticaAgrupadaResponse[];
    reunioesDuracaoFaixa: ComissaoEstatisticaAgrupadaResponse[];
    reunioesDuracaoModalidade: ComissaoEstatisticaAgrupadaResponse[];
    atasLeadTimeFaixa: ComissaoEstatisticaAgrupadaResponse[];
    atasLeadTimeComissao: ComissaoEstatisticaAgrupadaResponse[];
    reunioesLocalInternoExterno: ComissaoEstatisticaAgrupadaResponse[];
    reunioesLocal: ComissaoEstatisticaAgrupadaResponse[];
    reunioesPortal: ComissaoEstatisticaAgrupadaResponse[];
    atasImportancia: ComissaoEstatisticaAgrupadaResponse[];
    agendaSemanal: ComissaoEstatisticaAgrupadaResponse[];
    atasProducaoMensal: ComissaoEstatisticaAgrupadaResponse[];
    atividadesEvolucao: ComissaoEstatisticaAgrupadaResponse[];
    pendencias: ComissaoEstatisticaAgrupadaResponse[];
    atividadesSituacao: ComissaoEstatisticaAgrupadaResponse[];
    atividadesResponsavel: ComissaoEstatisticaAgrupadaResponse[];
    atividadesPrazo: ComissaoEstatisticaAgrupadaResponse[];
    membrosLgpd: ComissaoEstatisticaAgrupadaResponse[];
    membrosQuitacao: ComissaoEstatisticaAgrupadaResponse[];
    membrosParticipacao: ComissaoEstatisticaAgrupadaResponse[];
    membrosMultivinculo: ComissaoEstatisticaAgrupadaResponse[];
    membrosStatus: ComissaoEstatisticaAgrupadaResponse[];
    membrosUf: ComissaoEstatisticaAgrupadaResponse[];
    membrosEntradasMes: ComissaoEstatisticaAgrupadaResponse[];
    membrosMandatoTipo: ComissaoEstatisticaAgrupadaResponse[];
    membrosStatusHistorico: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoPresencas: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoEmails: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoPassagensMes: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoMeio: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoSmsStatus: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoEmailSms: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoConvidados: ComissaoEstatisticaAgrupadaResponse[];
    convocacaoReunioesConvidados: ComissaoEstatisticaAgrupadaResponse[];
    comissoesTipo: ComissaoEstatisticaAgrupadaResponse[];
    comissoesStatus: ComissaoEstatisticaAgrupadaResponse[];
    comissoesSemReuniao: ComissaoEstatisticaAgrupadaResponse[];
    comissoesExtintasAno: ComissaoEstatisticaAgrupadaResponse[];
    comissoesVidaMediaTipo: ComissaoEstatisticaAgrupadaResponse[];
    comissoesEntidadesExternas: ComissaoEstatisticaAgrupadaResponse[];
    comissoesComSemNucleos: ComissaoEstatisticaAgrupadaResponse[];
    comissoesNucleosSituacao: ComissaoEstatisticaAgrupadaResponse[];
    comissoesFaixaMembros: ComissaoEstatisticaAgrupadaResponse[];
    comissoesCoordenador: ComissaoEstatisticaAgrupadaResponse[];
    comissoesAntiguidade: ComissaoEstatisticaAgrupadaResponse[];
    atividadesComissao: ComissaoEstatisticaAgrupadaResponse[];
    atividadesTempoConclusao: ComissaoEstatisticaAgrupadaResponse[];
    atividadesOrigem: ComissaoEstatisticaAgrupadaResponse[];
    atividadesProtocolo: ComissaoEstatisticaAgrupadaResponse[];
    funil: ComissaoEstatisticaFunilResponse[];
    confirmacoes: ComissaoEstatisticaConfirmacaoResponse[];
}

@Component({
    selector: 'app-dashboard-gestao-comissoes',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonModule,
        CardContent,
        ChartModule,
        DatePicker,
        DialogModule,
        Razao,
        SelectEstado,
        SkeletonModule,
        TagModule,
        DetalhamentoPendencias,
        ConselheirosEstatisticasComponent,
        PainelComissoesContainer
    ],
    templateUrl: './dashboard-gestao-comissoes.html',
    styleUrls: ['./dashboard-gestao-comissoes.scss']
})
export class DashboardComponent implements OnInit {
    private readonly reuniaoService = inject(ComissaoReuniaoService);
    private readonly atividadeService = inject(ComissaoAtividadeService);
    private readonly estatisticaService = inject(ComissaoEstatisticaService);
    private readonly authService = inject(AuthService);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    private ufTokenRestrita: string | null = null;

    readonly hoje = this.formatarDataInput(new Date());
    readonly inicioAno = this.formatarDataInput(new Date(new Date().getFullYear(), 0, 1));
    readonly fimAno = this.formatarDataInput(new Date(new Date().getFullYear(), 11, 31));
    dataInicioDate: Date | null = new Date(new Date().getFullYear(), 0, 1);
    dataFimDate: Date | null = new Date(new Date().getFullYear(), 11, 31);
    dataInicioEncerramentoDate: Date | null = null;
    dataFimEncerramentoDate: Date | null = null;

    codigoReuniaoFiltro: number | null = null;
    idStatusReuniaoFiltro: number | null = null;
    idTipoComissaoCtFiltro: number | null = null;
    idEstruturaOrganizacionalFiltro: number | null = null;
    idStatusAtaFiltro: number | null = null;
    idLocalReuniaoFiltro: number | null = null;
    tipoConvocacaoFiltro: EComissaoReuniaoTipoConvocacaoFiltro = EComissaoReuniaoTipoConvocacaoFiltro.TODOS;
    marcacaoAtaFiltro: EComissaoReuniaoMarcacaoAtaFiltro = EComissaoReuniaoMarcacaoAtaFiltro.TODAS;
    idSolicitanteAssistenteFiltro: number | null = null;
    ufSolicitanteAssistenteFiltro = '';
    idMembroConvidadoFiltro: number | null = null;
    ufMembroConvidadoFiltro = '';
    idSituacaoTarefaFiltro: number | null = null;
    idResponsavelAtividadeFiltro: number | null = null;
    ufResponsavelAtividadeFiltro = '';
    descricaoAtividadeFiltro = '';

    readonly opcoesStatusReuniao: OpcaoFiltro<number | null>[] = [
        { label: 'Todos', value: null },
        { label: 'Agendada', value: 1 },
        { label: 'Remarcada', value: 2 },
        { label: 'Cancelada', value: 3 },
        { label: 'Realizada', value: 4 },
        { label: 'Excluída', value: 5 }
    ];
    readonly opcoesConvocacao: OpcaoFiltro<EComissaoReuniaoTipoConvocacaoFiltro>[] = [
        { label: 'Todas', value: EComissaoReuniaoTipoConvocacaoFiltro.TODOS },
        { label: 'Convocações pendentes', value: EComissaoReuniaoTipoConvocacaoFiltro.CONVOCACOES_PENDENTES },
        { label: 'Confirmações pendentes', value: EComissaoReuniaoTipoConvocacaoFiltro.CONFIRMACOES_PENDENTES }
    ];
    readonly opcoesMarcacaoAta: OpcaoFiltro<EComissaoReuniaoMarcacaoAtaFiltro>[] = [
        { label: 'Todas', value: EComissaoReuniaoMarcacaoAtaFiltro.TODAS },
        { label: 'Não importantes', value: EComissaoReuniaoMarcacaoAtaFiltro.NAO_IMPORTANTE },
        { label: 'Importantes', value: EComissaoReuniaoMarcacaoAtaFiltro.IMPORTANTE }
    ];
    opcoesStatusAta: OpcaoFiltro<number | null>[] = [{ label: 'Todos', value: null }];
    opcoesLocaisReuniao: OpcaoFiltro<number | null>[] = [{ label: 'Todos', value: null }];
    opcoesTiposComissao: OpcaoFiltro<number | null>[] = [{ label: 'Todos', value: null }];
    opcoesSituacaoAtividade: OpcaoFiltro<number | null>[] = [{ label: 'Todas', value: null }];

    get dataInicio(): string {
        return this.dataInicioDate ? this.formatarDataInput(this.dataInicioDate) : '';
    }
    set dataInicio(valor: string) {
        if (valor) {
            const [ano, mes, dia] = valor.split('-').map(Number);
            this.dataInicioDate = new Date(ano, mes - 1, dia);
        } else {
            this.dataInicioDate = null;
        }
    }

    get dataFim(): string {
        return this.dataFimDate ? this.formatarDataInput(this.dataFimDate) : '';
    }
    set dataFim(valor: string) {
        if (valor) {
            const [ano, mes, dia] = valor.split('-').map(Number);
            this.dataFimDate = new Date(ano, mes - 1, dia);
        } else {
            this.dataFimDate = null;
        }
    }

    get dataInicioEncerramento(): string {
        return this.dataInicioEncerramentoDate ? this.formatarDataInput(this.dataInicioEncerramentoDate) : '';
    }

    get dataFimEncerramento(): string {
        return this.dataFimEncerramentoDate ? this.formatarDataInput(this.dataFimEncerramentoDate) : '';
    }

    get totalAtasProtocolo(): number {
        return this.somarTotal(this.dadosAtasProtocolo);
    }

    get totalPendenciasConvocacao(): number {
        return this.somarTotal(this.dadosPendenciasConvocacao);
    }

    get totalAtividadesPrazo(): number {
        return this.somarTotal(this.dadosAtividadesPrazo);
    }

    get totalAtividadesResponsavel(): number {
        return this.somarTotal(this.dadosAtividadesResponsavel);
    }

    get totalMembrosLgpd(): number {
        return this.somarTotal(this.dadosMembrosLgpd);
    }

    get totalMembrosQuitacao(): number {
        return this.somarTotal(this.dadosMembrosQuitacao);
    }

    get totalAtividadesComissao(): number {
        return this.somarTotal(this.dadosAtividadesComissao);
    }

    get totalAtividadesTempoConclusao(): number {
        return this.somarTotal(this.dadosAtividadesTempoConclusao);
    }

    get totalAtividadesOrigem(): number {
        return this.somarTotal(this.dadosAtividadesOrigem);
    }

    get totalAtividadesProtocolo(): number {
        return this.somarTotal(this.dadosAtividadesProtocolo);
    }

    get totalAgendaSemanal(): number {
        return this.somarTotal(this.dadosAgendaSemanal);
    }

    get totalAtasProducaoMensal(): number {
        return this.somarTotal(this.dadosAtasProducaoMensal);
    }

    get totalAtividadesEvolucao(): number {
        return this.somarTotal(this.dadosAtividadesEvolucao);
    }

    get totalMembrosParticipacao(): number {
        return this.somarTotal(this.dadosMembrosParticipacao);
    }

    get totalMembrosMultivinculo(): number {
        return this.somarTotal(this.dadosMembrosMultivinculo);
    }

    get totalMembrosStatus(): number {
        return this.somarTotal(this.dadosMembrosStatus);
    }

    get totalMembrosUf(): number {
        return this.somarTotal(this.dadosMembrosUf);
    }

    get totalMembrosEntradasMes(): number {
        return this.somarTotal(this.dadosMembrosEntradasMes);
    }

    get totalMembrosMandatoTipo(): number {
        return this.somarTotal(this.dadosMembrosMandatoTipo);
    }

    get totalMembrosStatusHistorico(): number {
        return this.somarTotal(this.dadosMembrosStatusHistorico);
    }

    get totalConvocacaoPresencas(): number {
        return this.somarTotal(this.dadosConvocacaoPresencas);
    }

    get totalConvocacaoEmails(): number {
        return this.somarTotal(this.dadosConvocacaoEmails);
    }

    get totalConvocacaoPassagensMes(): number {
        return this.somarTotal(this.dadosConvocacaoPassagensMes);
    }

    get totalConvocacaoMeio(): number {
        return this.somarTotal(this.dadosConvocacaoMeio);
    }

    get totalConvocacaoSmsStatus(): number {
        return this.somarTotal(this.dadosConvocacaoSmsStatus);
    }

    get totalConvocacaoEmailSms(): number {
        return this.somarTotal(this.dadosConvocacaoEmailSms);
    }

    get totalConvocacaoConvidados(): number {
        return this.somarTotal(this.dadosConvocacaoConvidados);
    }

    get totalConvocacaoReunioesConvidados(): number {
        return this.somarTotal(this.dadosConvocacaoReunioesConvidados);
    }

    get totalComissoesTipo(): number {
        return this.somarTotal(this.dadosComissoesTipo);
    }

    get totalComissoesStatus(): number {
        return this.somarTotal(this.dadosComissoesStatus);
    }

    get totalComissoesSemReuniao(): number {
        return this.somarTotal(this.dadosComissoesSemReuniao);
    }

    get totalComissoesExtintasAno(): number {
        return this.somarTotal(this.dadosComissoesExtintasAno);
    }

    get totalComissoesVidaMediaTipo(): number {
        return this.somarTotal(this.dadosComissoesVidaMediaTipo);
    }

    get totalComissoesEntidadesExternas(): number {
        return this.somarTotal(this.dadosComissoesEntidadesExternas);
    }

    get totalComissoesComSemNucleos(): number {
        return this.somarTotal(this.dadosComissoesComSemNucleos);
    }

    get totalComissoesNucleosSituacao(): number {
        return this.somarTotal(this.dadosComissoesNucleosSituacao);
    }

    get totalComissoesFaixaMembros(): number {
        return this.somarTotal(this.dadosComissoesFaixaMembros);
    }

    get totalComissoesCoordenador(): number {
        return this.somarTotal(this.dadosComissoesCoordenador);
    }

    get totalComissoesAntiguidade(): number {
        return this.somarTotal(this.dadosComissoesAntiguidade);
    }

    get totalAtosComissao(): number {
        return this.somarTotal(this.dadosAtosComissao);
    }

    get totalAtosDistribuicao(): number {
        return this.somarTotal(this.dadosAtosDistribuicao);
    }

    get totalQuorumMedioComissao(): number {
        return this.somarTotal(this.dadosQuorumMedioComissao);
    }

    get totalQuorumFaixa(): number {
        return this.somarTotal(this.dadosQuorumFaixa);
    }

    get totalReunioesDuracaoFaixa(): number {
        return this.somarTotal(this.dadosReunioesDuracaoFaixa);
    }

    get totalReunioesDuracaoModalidade(): number {
        return this.somarTotal(this.dadosReunioesDuracaoModalidade);
    }

    get totalAtasLeadTimeFaixa(): number {
        return this.somarTotal(this.dadosAtasLeadTimeFaixa);
    }

    get totalAtasLeadTimeComissao(): number {
        return this.somarTotal(this.dadosAtasLeadTimeComissao);
    }

    get totalReunioesLocalInternoExterno(): number {
        return this.somarTotal(this.dadosReunioesLocalInternoExterno);
    }

    carregando = false;
    erro = '';
    avisoPaginacao = '';

    /** Acumulador dos dados já carregados; cada seção preenche seu subconjunto sob demanda. */
    private resultadoDashboard: ResultadoDashboard = this.criarResultadoVazio();
    /** Indicadores executivos (KPI) já carregados nesta janela de filtro. */
    private kpiCarregado = false;
    /** Seções cujos dados já foram carregados nesta janela de filtro. */
    private readonly secoesCarregadas = new Set<string>();

    resumo: ComissaoReuniaoDashboardSummaryResponse | null = null;
    indicadores: IndicadorComissao[] = [];
    funilOperacional: ItemFunilComissao[] = [];
    confirmacoesRanking: ConfirmacaoReuniao[] = [];
    totalReunioes = 0;
    totalAtividades = 0;
    analiticoSelecionado: AnaliticoGraficoComissao | null = null;
    tipoAnaliticoSelecionado: AnaliticoGraficoComissaoId | null = null;
    grupoAnaliticoCodigo: string | null = null;
    linhasAnalitico: PendenciaLinha[] = [];
    firstAnalitico = 0;
    rowsAnalitico = 100;
    totalRegistrosAnalitico = 0;
    carregandoAnalitico = false;
    cardMaximizadoId: string | null = null;
    exibirModalMaximizados = false;

    /** Provedor de exportação do drill-down: carrega todas as páginas do analítico atual em CSV. */
    readonly provedorExportacaoAnalitico = (): Observable<PendenciaLinha[]> => this.carregarTodasLinhasAnalitico();

    readonly secoes: SecaoDashboard[] = [
        { id: 'reunioes', label: 'Reuniões', icon: 'pi pi-calendar', descricao: 'Volume, status, modalidade, agenda e responsáveis das reuniões.' },
        { id: 'atas', label: 'Atas', icon: 'pi pi-file-edit', descricao: 'Status, protocolo, importância e produção mensal das atas.' },
        { id: 'convocacoes', label: 'Convocações', icon: 'pi pi-send', descricao: 'Pendências, confirmações, presenças, e-mails e atos de concessão.' },
        { id: 'atividades', label: 'Atividades', icon: 'pi pi-briefcase', descricao: 'Situação, prazo, responsáveis, comissão e evolução das atividades.' },
        { id: 'membros', label: 'Membros', icon: 'pi pi-users', descricao: 'Composição, participação, LGPD, quitação e UF dos membros.' },
        { id: 'comissoes', label: 'Comissões', icon: 'pi pi-sitemap', descricao: 'Tipo, status e agenda futura das comissões/estruturas.' },
        { id: 'conselheiros', label: 'Conselheiros', icon: 'pi pi-id-card', descricao: 'Atuação, faltas, presenças e comissões dos conselheiros.' }
    ];
    readonly subsecoesDashboard: Record<string, SecaoDashboard[]> = {
        reunioes: [
            { id: 'visao-geral', label: 'Visão geral', icon: 'pi pi-chart-line', descricao: 'Funil, evolução mensal e próximos compromissos das reuniões.' },
            { id: 'distribuicao', label: 'Distribuição', icon: 'pi pi-chart-pie', descricao: 'Distribuição das reuniões por status, modalidade e tipo de comissão.' },
            { id: 'operacao', label: 'Operação', icon: 'pi pi-map-marker', descricao: 'Locais, visibilidade no portal e agenda semanal das reuniões.' }
        ],
        atas: [
            { id: 'visao-geral', label: 'Visão geral', icon: 'pi pi-file-edit', descricao: 'Produção mensal e status das atas vinculadas às reuniões.' },
            { id: 'qualidade', label: 'Qualidade', icon: 'pi pi-flag', descricao: 'Acompanhamento de protocolos e atas marcadas como importantes.' }
        ],
        convocacoes: [
            { id: 'confirmacao', label: 'Confirmação', icon: 'pi pi-check-circle', descricao: 'Taxas de confirmação, pendências e envio de e-mails de convocação.' },
            { id: 'presenca-atos', label: 'Presença e atos', icon: 'pi pi-ticket', descricao: 'Presenças registradas e atos de concessão associados às reuniões.' }
        ],
        atividades: [
            { id: 'visao-geral', label: 'Visão geral', icon: 'pi pi-chart-line', descricao: 'Evolução, situação e prazo das atividades vinculadas ao módulo.' },
            { id: 'distribuicao', label: 'Distribuição', icon: 'pi pi-users', descricao: 'Distribuição das atividades por responsável e comissão.' }
        ],
        membros: [
            { id: 'composicao', label: 'Composição', icon: 'pi pi-map', descricao: 'Distribuição dos membros por UF, participação e status.' },
            { id: 'conformidade', label: 'Conformidade', icon: 'pi pi-shield', descricao: 'Situação dos membros em relação à LGPD e quitação de débitos.' }
        ],
        comissoes: [
            { id: 'perfil', label: 'Perfil', icon: 'pi pi-sitemap', descricao: 'Distribuição das comissões por tipo, câmara ou grupo.' },
            { id: 'situacao', label: 'Situação', icon: 'pi pi-power-off', descricao: 'Acompanhamento de comissões ativas, inativas ou extintas.' },
            { id: 'agenda-futura', label: 'Agenda futura', icon: 'pi pi-calendar-times', descricao: 'Comissões ativas com e sem reunião agendada futura.' }
        ],
        conselheiros: [{ id: 'atuacao', label: 'Atuação', icon: 'pi pi-chart-bar', descricao: 'Atuação, faltas, presenças e comissões dos conselheiros.' }]
    };
    secaoSelecionada = 'reunioes';
    subsecaoSelecionada = '';

    private dadosReunioesStatus: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosReunioesEvolucao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosReunioesTipoComissao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtasStatus: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosAtasProtocolo: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosModalidade: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtosComissao: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosAtosDistribuicao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosQuorumMedioComissao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosQuorumFaixa: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosReunioesDuracaoFaixa: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosReunioesDuracaoModalidade: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtasLeadTimeFaixa: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtasLeadTimeComissao: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosReunioesLocalInternoExterno: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosReunioesLocal: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosReunioesPortal: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosAtasImportancia: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAgendaSemanal: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtasProducaoMensal: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesEvolucao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosPendenciasConvocacao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesSituacao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesResponsavel: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesPrazo: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosMembrosLgpd: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosMembrosQuitacao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosParticipacao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosMultivinculo: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosMembrosStatus: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosUf: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosEntradasMes: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosMandatoTipo: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosMembrosStatusHistorico: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosConvocacaoPresencas: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosConvocacaoEmails: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosConvocacaoPassagensMes: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosConvocacaoMeio: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosConvocacaoSmsStatus: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosConvocacaoEmailSms: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosConvocacaoConvidados: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosConvocacaoReunioesConvidados: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesTipo: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosComissoesStatus: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosComissoesSemReuniao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesExtintasAno: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesVidaMediaTipo: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesEntidadesExternas: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosComissoesComSemNucleos: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosComissoesNucleosSituacao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesFaixaMembros: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosComissoesCoordenador: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosComissoesAntiguidade: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesComissao: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosAtividadesTempoConclusao: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosAtividadesOrigem: ComissaoEstatisticaAgrupadaResponse[] = [];
    protected dadosAtividadesProtocolo: ComissaoEstatisticaAgrupadaResponse[] = [];
    private dadosFunil: ComissaoEstatisticaFunilResponse[] = [];
    private dadosConfirmacoes: ComissaoEstatisticaConfirmacaoResponse[] = [];

    reunioesStatusData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    reunioesEvolucaoData: ChartData<'bar'> = this.criarBarraVazia();
    reunioesTipoComissaoData: ChartData<'bar'> = this.criarBarraVazia();
    atasStatusData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    atasProtocoloData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    modalidadeData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    atosComissaoData: ChartData<'bar'> = this.criarBarraVazia();
    atosDistribuicaoData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    quorumMedioComissaoData: ChartData<'bar'> = this.criarBarraVazia();
    quorumFaixaData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    reunioesDuracaoFaixaData: ChartData<'bar'> = this.criarBarraVazia();
    reunioesDuracaoModalidadeData: ChartData<'bar'> = this.criarBarraVazia();
    atasLeadTimeFaixaData: ChartData<'bar'> = this.criarBarraVazia();
    atasLeadTimeComissaoData: ChartData<'bar'> = this.criarBarraVazia();
    reunioesLocalData: ChartData<'bar'> = this.criarBarraVazia();
    reunioesPortalData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    atasImportanciaData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    agendaSemanalData: ChartData<'bar'> = this.criarBarraVazia();
    atasProducaoMensalData: ChartData<'bar'> = this.criarBarraVazia();
    atividadesEvolucaoData: ChartData<'bar'> = this.criarBarraVazia();
    pendenciasConvocacaoData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    atividadesSituacaoData: ChartData<'bar'> = this.criarBarraVazia();
    atividadesResponsavelData: ChartData<'bar'> = this.criarBarraVazia();
    atividadesPrazoData: ChartData<'bar'> = this.criarBarraVazia();
    confirmacoesData: ChartData<'bar'> = this.criarBarraVazia();
    membrosLgpdData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    membrosQuitacaoData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    membrosParticipacaoData: ChartData<'bar'> = this.criarBarraVazia();
    membrosMultivinculoData: ChartData<'bar'> = this.criarBarraVazia();
    membrosStatusData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    membrosUfData: ChartData<'bar'> = this.criarBarraVazia();
    membrosEntradasMesData: ChartData<'bar'> = this.criarBarraVazia();
    membrosMandatoTipoData: ChartData<'bar'> = this.criarBarraVazia();
    membrosStatusHistoricoData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    convocacaoPresencasData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    convocacaoEmailsData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    convocacaoPassagensMesData: ChartData<'bar'> = this.criarBarraVazia();
    convocacaoMeioData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    convocacaoSmsStatusData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    convocacaoConvidadosData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesTipoData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesStatusData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    comissoesSemReuniaoData: ChartData<'doughnut'> = this.criarDoughnutVazio();
    comissoesExtintasAnoData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesVidaMediaTipoData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesEntidadesExternasData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesFaixaMembrosData: ChartData<'bar'> = this.criarBarraVazia();
    comissoesAntiguidadeData: ChartData<'bar'> = this.criarBarraVazia();
    atividadesComissaoData: ChartData<'bar'> = this.criarBarraVazia();
    atividadesTempoConclusaoData: ChartData<'bar'> = this.criarBarraVazia();

    readonly doughnutOptions: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: this.obterCorTextoGrafico(),
                    boxWidth: 10,
                    padding: 8,
                    font: { size: 10 }
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: TooltipItem<'doughnut'>) => {
                        const valor = this.numeroSeguro(context.parsed);
                        const total = (context.dataset.data as number[]).reduce((soma, item) => soma + this.numeroSeguro(item), 0);
                        const percentual = total > 0 ? Math.round((valor / total) * 100) : 0;
                        return ` ${context.label}: ${valor.toLocaleString('pt-BR')} (${percentual}%)`;
                    }
                }
            }
        }
    };

    readonly barOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context: TooltipItem<'bar'>) => ` ${context.label}: ${this.numeroSeguro(context.parsed.y).toLocaleString('pt-BR')}`
                }
            }
        },
        scales: {
            x: {
                ticks: { color: this.obterCorTextoGrafico() },
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: this.obterCorTextoGrafico()
                },
                grid: { color: this.obterCorGridGrafico() }
            }
        }
    };

    readonly horizontalBarOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context: TooltipItem<'bar'>) => ` ${context.label}: ${this.numeroSeguro(context.parsed.x)}%`
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                max: 100,
                ticks: {
                    color: this.obterCorTextoGrafico(),
                    callback: (value) => `${value}%`
                },
                grid: { color: this.obterCorGridGrafico() }
            },
            y: {
                ticks: {
                    color: this.obterCorTextoGrafico(),
                    autoSkip: false,
                    // Sem isto o Chart.js corta o rotulo pela esquerda e come o inicio do nome.
                    callback: truncarRotuloCategoria()
                },
                grid: { display: false }
            }
        }
    };

    readonly horizontalCountBarOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    // O eixo pode estar truncado: o tooltip mostra o nome completo.
                    title: tituloTooltipCompleto,
                    label: (context: TooltipItem<'bar'>) => ` ${this.numeroSeguro(context.parsed.x).toLocaleString('pt-BR')}`
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: this.obterCorTextoGrafico()
                },
                grid: { color: this.obterCorGridGrafico() }
            },
            y: {
                ticks: {
                    color: this.obterCorTextoGrafico(),
                    autoSkip: false,
                    // Sem isto o Chart.js corta o rotulo pela esquerda e come o inicio do nome.
                    callback: truncarRotuloCategoria()
                },
                grid: { display: false }
            }
        }
    };

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const secao = params.get('secao');
            this.secaoSelecionada = this.secoes.some((item) => item.id === secao) ? (secao as string) : this.secoes[0].id;
            const subsecao = params.get('subsecao');
            this.subsecaoSelecionada = this.obterSubsecaoValida(this.secaoSelecionada, subsecao);
            this.voltarParaGraficos();
            // Carrega sob demanda os dados da seção recém-selecionada (e os KPIs na 1ª vez).
            this.carregarSecaoAtual();
            // Os gráficos da seção recém-exibida estavam com display:none; força o recálculo de tamanho.
            setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
        });
        this.carregarOpcoesFiltros();
    }

    /** Recarrega o dashboard do zero (botão Atualizar / mudança de filtro): reinicia o cache e recarrega KPIs + seção atual. */
    carregarDashboard(): void {
        this.erro = '';
        this.avisoPaginacao = '';
        this.kpiCarregado = false;
        this.secoesCarregadas.clear();
        this.resultadoDashboard = this.criarResultadoVazio();
        this.carregarSecaoAtual();
    }

    /** Carrega, sob demanda, os indicadores executivos (1ª vez) e os dados da seção atual (uma vez por janela de filtro). */
    private carregarSecaoAtual(): void {
        const tarefas: Observable<Partial<ResultadoDashboard>>[] = [];
        if (!this.kpiCarregado) {
            tarefas.push(this.carregarBundleKpi());
        }

        const secao = this.secaoSelecionada;
        if (!this.secoesCarregadas.has(secao)) {
            const bundle = this.criarBundleSecao(secao);
            if (bundle) {
                tarefas.push(bundle);
            }
        }
        // Marca como carregada mesmo sem bundle próprio (ex.: "conselheiros", cujo subcomponente carrega os próprios dados).
        this.secoesCarregadas.add(secao);

        if (!tarefas.length) {
            return;
        }

        this.carregando = true;
        forkJoin(tarefas)
            .pipe(
                finalize(() => {
                    this.carregando = false;
                    // Sem isto os cards ficam no skeleton mesmo apos as consultas terminarem.
                    this.cdr.markForCheck();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((parciais) => {
                this.kpiCarregado = true;
                parciais.forEach((parcial) => (this.resultadoDashboard = { ...this.resultadoDashboard, ...parcial }));
                this.aplicarResultado();
                this.cdr.markForCheck();
            });
    }

    /** Indicadores executivos (KPI) exibidos no topo em todas as seções. */
    private carregarBundleKpi(): Observable<Partial<ResultadoDashboard>> {
        const filtroReunioes = this.criarFiltroReunioes();
        const filtroAtividades = this.criarFiltroAtividades();
        return forkJoin({
            resumo: this.reuniaoService.consultarDashboardSummary(filtroReunioes).pipe(catchError(() => of(null))),
            reunioesStatus: this.estatisticaService.listarReunioesPorStatus(filtroReunioes).pipe(catchError(() => of([]))),
            atasStatus: this.estatisticaService.listarAtasPorStatus(filtroReunioes).pipe(catchError(() => of([]))),
            atasProtocolo: this.estatisticaService.listarAtasPorProtocolo(filtroReunioes).pipe(catchError(() => of([]))),
            pendencias: this.estatisticaService.listarPendenciasConvocacao(filtroReunioes).pipe(catchError(() => of([]))),
            atividadesSituacao: this.estatisticaService.listarAtividadesPorSituacao(filtroAtividades).pipe(catchError(() => of([]))),
            atividadesPrazo: this.estatisticaService.listarAtividadesPorPrazo(filtroAtividades).pipe(catchError(() => of([])))
        });
    }

    /** Retorna o carregamento dos dados exclusivos de uma seção (ou null quando a seção não tem bundle próprio). */
    private criarBundleSecao(secao: string): Observable<Partial<ResultadoDashboard>> | null {
        const filtroReunioes = this.criarFiltroReunioes();
        const filtroAtividades = this.criarFiltroAtividades();

        switch (secao) {
            case 'reunioes':
                return forkJoin({
                    reunioesEvolucao: this.estatisticaService.listarReunioesEvolucaoMensal(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesTipoComissao: this.estatisticaService.listarReunioesPorTipoComissao(filtroReunioes).pipe(catchError(() => of([]))),
                    modalidade: this.estatisticaService.listarReunioesPorModalidade(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesLocal: this.estatisticaService.listarReunioesPorLocal(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesDuracaoFaixa: this.estatisticaService.listarReunioesDuracaoFaixa(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesDuracaoModalidade: this.estatisticaService.listarReunioesDuracaoModalidade(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesLocalInternoExterno: this.estatisticaService.listarReunioesLocalInternoExterno(filtroReunioes).pipe(catchError(() => of([]))),
                    reunioesPortal: this.estatisticaService.listarReunioesPorVisibilidadePortal(filtroReunioes).pipe(catchError(() => of([]))),
                    agendaSemanal: this.estatisticaService.listarReunioesAgendaSemanal(filtroReunioes).pipe(catchError(() => of([]))),
                    funil: this.estatisticaService.listarFunilOperacional(filtroReunioes).pipe(catchError(() => of([])))
                });
            case 'atas':
                return forkJoin({
                    atasProducaoMensal: this.estatisticaService.listarAtasProducaoMensal(filtroReunioes).pipe(catchError(() => of([]))),
                    atasImportancia: this.estatisticaService.listarAtasPorImportancia(filtroReunioes).pipe(catchError(() => of([]))),
                    atasLeadTimeFaixa: this.estatisticaService.listarAtasLeadTimeFaixa(filtroReunioes).pipe(catchError(() => of([]))),
                    atasLeadTimeComissao: this.estatisticaService.listarAtasLeadTimeComissao(filtroReunioes).pipe(catchError(() => of([])))
                });
            case 'convocacoes':
                return forkJoin({
                    confirmacoes: this.estatisticaService.listarConfirmacoesPorReuniao(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoEmails: this.estatisticaService.listarConvocacaoEmails(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoPassagensMes: this.estatisticaService.listarConvocacoesPassagensMes(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoMeio: this.estatisticaService.listarConvocacoesMeio(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoSmsStatus: this.estatisticaService.listarConvocacoesSmsStatus(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoEmailSms: this.estatisticaService.listarConvocacoesEmailSms(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoConvidados: this.estatisticaService.listarConvocacoesConvidados(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoReunioesConvidados: this.estatisticaService.listarConvocacoesReunioesConvidados(filtroReunioes).pipe(catchError(() => of([]))),
                    convocacaoPresencas: this.estatisticaService.listarConvocacaoPresencas(filtroReunioes).pipe(catchError(() => of([]))),
                    atosComissao: this.estatisticaService.listarAtosPorComissao(filtroReunioes).pipe(catchError(() => of([]))),
                    atosDistribuicao: this.estatisticaService.listarAtosDistribuicao(filtroReunioes).pipe(catchError(() => of([]))),
                    quorumMedioComissao: this.estatisticaService.listarQuorumMedioComissao(filtroReunioes).pipe(catchError(() => of([]))),
                    quorumFaixa: this.estatisticaService.listarQuorumFaixa(filtroReunioes).pipe(catchError(() => of([])))
                });
            case 'atividades':
                return forkJoin({
                    atividadesEvolucao: this.estatisticaService.listarAtividadesEvolucaoMensal(filtroAtividades).pipe(catchError(() => of([]))),
                    atividadesResponsavel: this.estatisticaService.listarAtividadesPorResponsavel(filtroAtividades).pipe(catchError(() => of([]))),
                    atividadesComissao: this.estatisticaService.listarAtividadesPorComissao(filtroAtividades).pipe(catchError(() => of([]))),
                    atividadesTempoConclusao: this.estatisticaService.listarAtividadesPorTempoConclusao(filtroAtividades).pipe(catchError(() => of([]))),
                    atividadesOrigem: this.estatisticaService.listarAtividadesPorOrigem(filtroAtividades).pipe(catchError(() => of([]))),
                    atividadesProtocolo: this.estatisticaService.listarAtividadesPorProtocolo(filtroAtividades).pipe(catchError(() => of([])))
                });
            case 'membros':
                return forkJoin({
                    membrosUf: this.estatisticaService.listarMembrosPorUf(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosParticipacao: this.estatisticaService.listarMembrosPorParticipacao(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosMultivinculo: this.estatisticaService.listarMembrosMultivinculo(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosStatus: this.estatisticaService.listarMembrosPorStatus(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosEntradasMes: this.estatisticaService.listarMembrosEntradasMes(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosMandatoTipo: this.estatisticaService.listarMembrosMandatoTipo(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosStatusHistorico: this.estatisticaService.listarMembrosStatusHistorico(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosLgpd: this.estatisticaService.listarMembrosLgpd(filtroReunioes).pipe(catchError(() => of([]))),
                    membrosQuitacao: this.estatisticaService.listarMembrosQuitacao(filtroReunioes).pipe(catchError(() => of([])))
                });
            case 'comissoes':
                return forkJoin({
                    comissoesTipo: this.estatisticaService.listarComissoesPorTipo(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesStatus: this.estatisticaService.listarComissoesPorStatus(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesSemReuniao: this.estatisticaService.listarComissoesSemReuniaoFutura(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesExtintasAno: this.estatisticaService.listarComissoesExtintasAno(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesVidaMediaTipo: this.estatisticaService.listarComissoesVidaMediaTipo(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesEntidadesExternas: this.estatisticaService.listarComissoesEntidadesExternas(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesComSemNucleos: this.estatisticaService.listarComissoesComSemNucleos(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesNucleosSituacao: this.estatisticaService.listarComissoesNucleosSituacao(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesFaixaMembros: this.estatisticaService.listarComissoesFaixaMembros(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesCoordenador: this.estatisticaService.listarComissoesCoordenador(filtroReunioes).pipe(catchError(() => of([]))),
                    comissoesAntiguidade: this.estatisticaService.listarComissoesAntiguidade(filtroReunioes).pipe(catchError(() => of([])))
                });
            default:
                return null;
        }
    }

    /** Reflete o acumulador atual nos campos de dados, indicadores e gráficos consumidos pelo template. */
    private aplicarResultado(): void {
        const resultado = this.resultadoDashboard;
        this.resumo = resultado.resumo;
        this.dadosReunioesStatus = resultado.reunioesStatus;
        this.dadosReunioesEvolucao = resultado.reunioesEvolucao;
        this.dadosReunioesTipoComissao = resultado.reunioesTipoComissao;
        this.dadosAtasStatus = resultado.atasStatus;
        this.dadosAtasProtocolo = resultado.atasProtocolo;
        this.dadosModalidade = resultado.modalidade;
        this.dadosAtosComissao = resultado.atosComissao;
        this.dadosAtosDistribuicao = resultado.atosDistribuicao;
        this.dadosQuorumMedioComissao = resultado.quorumMedioComissao;
        this.dadosQuorumFaixa = resultado.quorumFaixa;
        this.dadosReunioesDuracaoFaixa = resultado.reunioesDuracaoFaixa;
        this.dadosReunioesDuracaoModalidade = resultado.reunioesDuracaoModalidade;
        this.dadosAtasLeadTimeFaixa = resultado.atasLeadTimeFaixa;
        this.dadosAtasLeadTimeComissao = resultado.atasLeadTimeComissao;
        this.dadosReunioesLocalInternoExterno = resultado.reunioesLocalInternoExterno;
        this.dadosReunioesLocal = resultado.reunioesLocal;
        this.dadosReunioesPortal = resultado.reunioesPortal;
        this.dadosAtasImportancia = resultado.atasImportancia;
        this.dadosAgendaSemanal = resultado.agendaSemanal;
        this.dadosAtasProducaoMensal = resultado.atasProducaoMensal;
        this.dadosAtividadesEvolucao = resultado.atividadesEvolucao;
        this.dadosPendenciasConvocacao = resultado.pendencias;
        this.dadosAtividadesSituacao = resultado.atividadesSituacao;
        this.dadosAtividadesResponsavel = resultado.atividadesResponsavel;
        this.dadosAtividadesPrazo = resultado.atividadesPrazo;
        this.dadosMembrosLgpd = resultado.membrosLgpd;
        this.dadosMembrosQuitacao = resultado.membrosQuitacao;
        this.dadosMembrosParticipacao = resultado.membrosParticipacao;
        this.dadosMembrosMultivinculo = resultado.membrosMultivinculo;
        this.dadosMembrosStatus = resultado.membrosStatus;
        this.dadosMembrosUf = resultado.membrosUf;
        this.dadosMembrosEntradasMes = resultado.membrosEntradasMes;
        this.dadosMembrosMandatoTipo = resultado.membrosMandatoTipo;
        this.dadosMembrosStatusHistorico = resultado.membrosStatusHistorico;
        this.dadosConvocacaoPresencas = resultado.convocacaoPresencas;
        this.dadosConvocacaoEmails = resultado.convocacaoEmails;
        this.dadosConvocacaoPassagensMes = resultado.convocacaoPassagensMes;
        this.dadosConvocacaoMeio = resultado.convocacaoMeio;
        this.dadosConvocacaoSmsStatus = resultado.convocacaoSmsStatus;
        this.dadosConvocacaoEmailSms = resultado.convocacaoEmailSms;
        this.dadosConvocacaoConvidados = resultado.convocacaoConvidados;
        this.dadosConvocacaoReunioesConvidados = resultado.convocacaoReunioesConvidados;
        this.dadosComissoesTipo = resultado.comissoesTipo;
        this.dadosComissoesStatus = resultado.comissoesStatus;
        this.dadosComissoesSemReuniao = resultado.comissoesSemReuniao;
        this.dadosComissoesExtintasAno = resultado.comissoesExtintasAno;
        this.dadosComissoesVidaMediaTipo = resultado.comissoesVidaMediaTipo;
        this.dadosComissoesEntidadesExternas = resultado.comissoesEntidadesExternas;
        this.dadosComissoesComSemNucleos = resultado.comissoesComSemNucleos;
        this.dadosComissoesNucleosSituacao = resultado.comissoesNucleosSituacao;
        this.dadosComissoesFaixaMembros = resultado.comissoesFaixaMembros;
        this.dadosComissoesCoordenador = resultado.comissoesCoordenador;
        this.dadosComissoesAntiguidade = resultado.comissoesAntiguidade;
        this.dadosAtividadesComissao = resultado.atividadesComissao;
        this.dadosAtividadesTempoConclusao = resultado.atividadesTempoConclusao;
        this.dadosAtividadesOrigem = resultado.atividadesOrigem;
        this.dadosAtividadesProtocolo = resultado.atividadesProtocolo;
        this.dadosFunil = resultado.funil;
        this.dadosConfirmacoes = resultado.confirmacoes;
        this.totalReunioes = this.somarTotal(resultado.reunioesStatus);
        this.totalAtividades = this.somarTotal(resultado.atividadesSituacao);
        this.atualizarIndicadores(resultado);
        this.atualizarGraficos(resultado);
        this.atualizarAnaliticoSelecionado();
    }

    private criarResultadoVazio(): ResultadoDashboard {
        return {
            resumo: null,
            reunioesStatus: [],
            reunioesEvolucao: [],
            reunioesTipoComissao: [],
            atasStatus: [],
            atasProtocolo: [],
            modalidade: [],
            atosComissao: [],
            atosDistribuicao: [],
            quorumMedioComissao: [],
            quorumFaixa: [],
            reunioesDuracaoFaixa: [],
            reunioesDuracaoModalidade: [],
            atasLeadTimeFaixa: [],
            atasLeadTimeComissao: [],
            reunioesLocalInternoExterno: [],
            reunioesLocal: [],
            reunioesPortal: [],
            atasImportancia: [],
            agendaSemanal: [],
            atasProducaoMensal: [],
            atividadesEvolucao: [],
            pendencias: [],
            atividadesSituacao: [],
            atividadesResponsavel: [],
            atividadesPrazo: [],
            membrosLgpd: [],
            membrosQuitacao: [],
            membrosParticipacao: [],
            membrosMultivinculo: [],
            membrosStatus: [],
            membrosUf: [],
            membrosEntradasMes: [],
            membrosMandatoTipo: [],
            membrosStatusHistorico: [],
            convocacaoPresencas: [],
            convocacaoEmails: [],
            convocacaoPassagensMes: [],
            convocacaoMeio: [],
            convocacaoSmsStatus: [],
            convocacaoEmailSms: [],
            convocacaoConvidados: [],
            convocacaoReunioesConvidados: [],
            comissoesTipo: [],
            comissoesStatus: [],
            comissoesSemReuniao: [],
            comissoesExtintasAno: [],
            comissoesVidaMediaTipo: [],
            comissoesEntidadesExternas: [],
            comissoesComSemNucleos: [],
            comissoesNucleosSituacao: [],
            comissoesFaixaMembros: [],
            comissoesCoordenador: [],
            comissoesAntiguidade: [],
            atividadesComissao: [],
            atividadesTempoConclusao: [],
            atividadesOrigem: [],
            atividadesProtocolo: [],
            funil: [],
            confirmacoes: []
        };
    }

    maximizarGrafico(id: string): void {
        this.cardMaximizadoId = id;
        this.exibirModalMaximizados = true;
    }

    secaoAtiva(id: string): boolean {
        return this.secaoSelecionada === id;
    }

    subsecaoAtiva(secao: string, subsecao: string): boolean {
        return this.secaoAtiva(secao) && this.subsecaoSelecionada === subsecao;
    }

    obterSecaoSelecionada(): SecaoDashboard {
        const subsecoes = this.obterSubsecoesSecao(this.secaoSelecionada);
        if (subsecoes.length) {
            return subsecoes.find((secao) => secao.id === this.subsecaoSelecionada) ?? subsecoes[0];
        }

        return this.secoes.find((secao) => secao.id === this.secaoSelecionada) ?? this.secoes[0];
    }

    private obterSubsecaoValida(secao: string, subsecao: string | null): string {
        const subsecoes = this.obterSubsecoesSecao(secao);
        if (!subsecoes.length) {
            return '';
        }

        return subsecoes.some((item) => item.id === subsecao) ? (subsecao as string) : subsecoes[0].id;
    }

    private obterSubsecoesSecao(secao: string): SecaoDashboard[] {
        return this.subsecoesDashboard[secao] ?? [];
    }

    obterIndicadorPorId(id: string): IndicadorComissao | null {
        const index = parseInt(id.replace('indicador-', ''), 10);
        if (!Number.isNaN(index) && this.indicadores[index]) {
            return this.indicadores[index];
        }
        return null;
    }

    obterTituloCardMaximizado(): string {
        if (this.cardMaximizadoId?.startsWith('indicador-')) {
            const ind = this.obterIndicadorPorId(this.cardMaximizadoId);
            return ind ? ind.titulo : 'Indicador';
        }
        switch (this.cardMaximizadoId) {
            case 'proxima-reuniao':
                return 'Próxima reunião';
            case 'ultima-reuniao':
                return 'Última reunião';
            case 'funil':
                return 'Funil operacional';
            case 'reunioes-status':
                return 'Reuniões por status';
            case 'reunioes-evolucao':
                return 'Evolução mensal de reuniões';
            case 'reunioes-tipo-comissao':
                return 'Reuniões por tipo de comissão';
            case 'atas-status':
                return 'Atas por status';
            case 'atas-protocolo':
                return 'Atas com e sem protocolo';
            case 'modalidade':
                return 'Reuniões por modalidade';
            case 'reunioes-atos-comissao':
                return 'Atos de concessão por comissão';
            case 'reunioes-atos-distribuicao':
                return 'Atos de concessão: com x sem';
            case 'quorum-medio-comissao':
                return 'Quórum médio por comissão';
            case 'quorum-faixa':
                return 'Reuniões por faixa de quórum';
            case 'reunioes-duracao-faixa':
                return 'Reuniões por faixa de duração';
            case 'reunioes-duracao-modalidade':
                return 'Duração média por modalidade';
            case 'atas-lead-time-faixa':
                return 'Atas por faixa de lead time';
            case 'atas-lead-time-comissao':
                return 'Lead time médio de ata por comissão';
            case 'reunioes-local-interno-externo':
                return 'Reuniões por local interno x externo';
            case 'reunioes-local':
                return 'Reuniões por local';
            case 'reunioes-portal':
                return 'Reuniões visíveis no portal';
            case 'atas-importancia':
                return 'Atas importantes';
            case 'agenda-semanal':
                return 'Agenda futura por semana';
            case 'atas-producao-mensal':
                return 'Produção mensal de atas';
            case 'atividades-evolucao':
                return 'Evolução mensal de atividades';
            case 'pendencias-convocacao':
                return 'Pendências de convocação';
            case 'atividades-situacao':
                return 'Atividades por situação';
            case 'atividades-responsavel':
                return 'Atividades por responsável';
            case 'atividades-prazo':
                return 'Atividades por prazo';
            case 'confirmacoes':
                return 'Taxa de confirmação por reunião';
            case 'membros-lgpd':
                return 'Membros por status LGPD';
            case 'membros-quitacao':
                return 'Membros por quitação de débitos';
            case 'membros-participacao':
                return 'Membros por participação';
            case 'membros-multivinculo':
                return 'Membros por nº de comissões';
            case 'membros-status':
                return 'Membros ativos x afastados';
            case 'membros-uf':
                return 'Membros por UF';
            case 'membros-entradas-mes':
                return 'Entradas de membros por mês';
            case 'membros-mandato-tipo':
                return 'Tempo médio de mandato por tipo (meses)';
            case 'membros-status-historico':
                return 'Membros por status (histórico)';
            case 'convocacoes-presencas':
                return 'Presenças por tipo';
            case 'convocacoes-emails':
                return 'Envio de e-mails de convocação';
            case 'convocacoes-passagens-mes':
                return 'Passagens emitidas por mês';
            case 'convocacoes-meio':
                return 'Confirmações por meio';
            case 'convocacoes-convidados':
                return 'Convidados mais frequentes';
            case 'convocacoes-reunioes-convidados':
                return 'Reuniões com x sem convidados';
            case 'comissoes-tipo':
                return 'Comissões por tipo';
            case 'comissoes-status':
                return 'Comissões ativas x inativas';
            case 'comissoes-sem-reuniao':
                return 'Comissões sem reunião futura';
            case 'comissoes-extintas-ano':
                return 'Comissões extintas por ano';
            case 'comissoes-vida-media-tipo':
                return 'Vida média por tipo';
            case 'comissoes-entidades-externas':
                return 'Entidades externas por representantes';
            case 'comissoes-com-sem-nucleos':
                return 'Comissões com vs sem núcleos';
            case 'comissoes-nucleos-situacao':
                return 'Núcleos ativos x encerrados';
            case 'comissoes-faixa-membros':
                return 'Comissões por faixa de nº de membros';
            case 'comissoes-coordenador':
                return 'Comissões com vs sem coordenador';
            case 'comissoes-antiguidade':
                return 'Comissões por antiguidade';
            case 'atividades-comissao':
                return 'Atividades por comissão';
            case 'atividades-tempo-conclusao':
                return 'Tarefas por faixa de tempo de conclusão';
            case 'atividades-origem':
                return 'Tarefas vinculadas x avulsas';
            case 'atividades-protocolo':
                return 'Tarefas com x sem protocolo';
            default:
                return '';
        }
    }

    limparFiltros(): void {
        this.dataInicio = this.inicioAno;
        this.dataFim = this.fimAno;
        this.ufSelecionada = this.ufTokenRestrita;
        this.carregarDashboard();
    }

    onUfChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUf(uf);
        this.carregarDashboard();
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        if (/^[A-Z]{2}$/.test(ufToken) && ufToken !== 'BR') {
            this.ufTokenRestrita = ufToken;
            this.ufsPermitidas = [ufToken];
            this.ufSelecionada = ufToken;
        } else {
            this.ufTokenRestrita = null;
            this.ufsPermitidas = null;
            this.ufSelecionada = null;
        }
    }

    private normalizarUf(uf?: string | null): string | null {
        const valor = String(uf ?? '')
            .trim()
            .toUpperCase();
        if (!valor || valor === 'BR' || !/^[A-Z]{2}$/.test(valor)) {
            return this.ufTokenRestrita;
        }
        if (this.ufTokenRestrita && valor !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }
        return valor;
    }

    aplicarFiltrosAnalitico(): void {
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnalitico();
    }

    limparFiltrosAnalitico(): void {
        this.dataInicioEncerramentoDate = null;
        this.dataFimEncerramentoDate = null;
        this.codigoReuniaoFiltro = null;
        this.idStatusReuniaoFiltro = null;
        this.idTipoComissaoCtFiltro = null;
        this.idEstruturaOrganizacionalFiltro = null;
        this.idStatusAtaFiltro = null;
        this.idLocalReuniaoFiltro = null;
        this.tipoConvocacaoFiltro = EComissaoReuniaoTipoConvocacaoFiltro.TODOS;
        this.marcacaoAtaFiltro = EComissaoReuniaoMarcacaoAtaFiltro.TODAS;
        this.idSolicitanteAssistenteFiltro = null;
        this.ufSolicitanteAssistenteFiltro = '';
        this.idMembroConvidadoFiltro = null;
        this.ufMembroConvidadoFiltro = '';
        this.idSituacaoTarefaFiltro = null;
        this.idResponsavelAtividadeFiltro = null;
        this.ufResponsavelAtividadeFiltro = '';
        this.descricaoAtividadeFiltro = '';
        this.aplicarFiltrosAnalitico();
    }

    obterPeriodoExibicao(): string {
        return `${this.formatarDataCurta(this.dataInicio)} a ${this.formatarDataCurta(this.dataFim)}`;
    }

    formatarDataHora(valor?: string | null): string {
        if (!valor) {
            return '-';
        }
        const data = new Date(valor);
        if (Number.isNaN(data.getTime())) {
            return '-';
        }
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(data);
    }

    selecionarAnalitico(tipo: AnaliticoGraficoComissaoId): void {
        this.selecionarAnaliticoComGrupo(tipo);
    }

    selecionarAnaliticoComGrupo(tipo: AnaliticoGraficoComissaoId, grupoPreferido?: string): void {
        const opcoes = this.obterOpcoesGrupoAnalitico(tipo);
        const preferido = grupoPreferido?.trim().toUpperCase();
        let escolhida = opcoes[0];
        if (preferido) {
            escolhida = opcoes.find((opcao) => (opcao.codigo ?? '').trim().toUpperCase() === preferido) ?? opcoes.find((opcao) => (opcao.descricao ?? '').trim().toUpperCase().includes(preferido)) ?? opcoes[0];
        }

        this.tipoAnaliticoSelecionado = tipo;
        this.grupoAnaliticoCodigo = escolhida?.codigo ?? null;
        this.analiticoSelecionado = this.criarAnalitico(tipo);
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregarAnalitico();
    }

    abrirAnaliticoCard(indicador: IndicadorComissao): void {
        if (!indicador.analiticoTipo) {
            return;
        }
        this.selecionarAnaliticoComGrupo(indicador.analiticoTipo, indicador.analiticoGrupo);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    voltarParaGraficos(): void {
        this.analiticoSelecionado = null;
        this.tipoAnaliticoSelecionado = null;
        this.grupoAnaliticoCodigo = null;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnalitico();
    }

    onGrupoAnaliticoChange(codigo: string): void {
        this.grupoAnaliticoCodigo = codigo;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.analiticoSelecionado = this.tipoAnaliticoSelecionado ? this.criarAnalitico(this.tipoAnaliticoSelecionado) : null;
        this.carregarAnalitico();
    }

    obterOpcoesGrupoAnalitico(tipo: AnaliticoGraficoComissaoId | null = this.tipoAnaliticoSelecionado): ComissaoEstatisticaAgrupadaResponse[] {
        if (!tipo) {
            return [];
        }

        if (tipo === 'confirmacoes') {
            return this.dadosConfirmacoes
                .filter((item) => !!item.idReuniao && this.numeroSeguro(item.totalConvocacoes) > 0)
                .map((item, index) => ({
                    codigo: String(item.idReuniao),
                    descricao: `#${item.idReuniao} ${item.dsEstruturaOrganizacional || 'Comissão'}`,
                    total: this.numeroSeguro(item.totalConvocacoes),
                    ordem: item.ordem ?? index + 1
                }));
        }

        return this.obterFonteOpcoesAnalitico(tipo).filter((item) => !!item.codigo && this.numeroSeguro(item.total) > 0);
    }

    obterDescricaoItem(item: ComissaoEstatisticaAgrupadaResponse): string {
        return item.descricao?.trim() || item.codigo?.trim() || 'Não informado';
    }

    obterMembrosUfItens(): ComissaoEstatisticaAgrupadaResponse[] {
        return this.dadosMembrosUf.filter((item) => this.numeroSeguro(item.total) > 0);
    }

    obterUfMembro(item: ComissaoEstatisticaAgrupadaResponse): string {
        const codigo = item.codigo?.trim().toUpperCase();

        if (codigo && /^[A-Z]{2}$/.test(codigo)) {
            return codigo;
        }

        const descricao = item.descricao?.trim().toUpperCase();

        if (descricao && /^[A-Z]{2}$/.test(descricao)) {
            return descricao;
        }

        const texto = `${item.codigo ?? ''} ${item.descricao ?? ''}`.toUpperCase();
        const ufs = texto.match(/\b[A-Z]{2}\b/g) ?? [];
        return ufs[0] ?? '';
    }

    obterBandeiraUf(uf?: string | null): string | null {
        const sigla = (uf ?? '').trim().toUpperCase();

        if (!/^[A-Z]{2}$/.test(sigla)) {
            return null;
        }

        return `images/estados/${sigla}.png`;
    }

    obterLarguraBarraMembrosUf(item: ComissaoEstatisticaAgrupadaResponse): string {
        const total = this.numeroSeguro(item.total);
        const maiorTotal = this.obterMaiorTotalMembrosUf();

        if (!total || !maiorTotal) {
            return '0%';
        }

        return `${Math.max(6, Math.round((total / maiorTotal) * 100))}%`;
    }

    exibirFiltrosReuniaoAnalitico(): boolean {
        return !!this.tipoAnaliticoSelecionado && !this.exibirFiltrosAtividadeAnalitico() && !this.exibirFiltrosMembroAnalitico();
    }

    exibirFiltrosAtaAnalitico(): boolean {
        return this.tipoAnaliticoSelecionado === 'atas-status' || this.tipoAnaliticoSelecionado === 'atas-protocolo' || this.tipoAnaliticoSelecionado === 'atas-importancia';
    }

    exibirFiltrosConvocacaoAnalitico(): boolean {
        return this.tipoAnaliticoSelecionado === 'pendencias-convocacao' || this.tipoAnaliticoSelecionado === 'confirmacoes';
    }

    exibirFiltrosAtividadeAnalitico(): boolean {
        return (
            this.tipoAnaliticoSelecionado === 'atividades-situacao' ||
            this.tipoAnaliticoSelecionado === 'atividades-responsavel' ||
            this.tipoAnaliticoSelecionado === 'atividades-prazo' ||
            this.tipoAnaliticoSelecionado === 'atividades-comissao' ||
            this.tipoAnaliticoSelecionado === 'atividades-evolucao' ||
            this.tipoAnaliticoSelecionado === 'atividades-tempo-conclusao'
        );
    }

    exibirFiltrosMembroAnalitico(): boolean {
        return (
            this.tipoAnaliticoSelecionado === 'membros-lgpd' ||
            this.tipoAnaliticoSelecionado === 'membros-quitacao' ||
            this.tipoAnaliticoSelecionado === 'membros-participacao' ||
            this.tipoAnaliticoSelecionado === 'membros-multivinculo' ||
            this.tipoAnaliticoSelecionado === 'membros-status' ||
            this.tipoAnaliticoSelecionado === 'membros-uf'
        );
    }

    exibirFiltrosComissaoAnalitico(): boolean {
        return !!this.tipoAnaliticoSelecionado;
    }

    obterIndicadorAnalitico(): IndicadorPendencia | null {
        if (!this.analiticoSelecionado) {
            return null;
        }

        const grupoSelecionado = this.obterGrupoAnaliticoSelecionado();
        return {
            ...this.analiticoSelecionado.indicador,
            descricao: grupoSelecionado ? `${this.analiticoSelecionado.indicador.descricao} Grupo: ${this.obterDescricaoItem(grupoSelecionado)}.` : this.analiticoSelecionado.indicador.descricao,
            quantidade: this.totalRegistrosAnalitico || null
        };
    }

    private atualizarIndicadores(resultado: ResultadoDashboard): void {
        const reunioesAgendadas = this.resumo?.totalAgendadas ?? this.buscarTotal(resultado.reunioesStatus, 'AGEND');
        const atasPendentes = this.somarPorDescricao(resultado.atasStatus, 'PEND');
        const comProtocolo = this.buscarTotalPorCodigo(resultado.atasProtocolo, 'COM_PROTOCOLO');
        const convocacoesPendentes = Math.max(this.buscarTotalPorCodigo(resultado.pendencias, 'CONVOCACOES_PENDENTES'), this.buscarTotalPorCodigo(resultado.pendencias, 'CONFIRMACOES_PENDENTES'));
        const atividadesPendentes = this.buscarTotalPorCodigo(resultado.atividadesPrazo, 'ATRASADAS') + this.buscarTotalPorCodigo(resultado.atividadesPrazo, 'NO_PRAZO');

        this.indicadores = [
            {
                titulo: 'Reuniões agendadas',
                valor: this.formatarNumero(reunioesAgendadas),
                legenda: `Período ${this.obterPeriodoExibicao()}`,
                icon: 'pi pi-calendar-clock',
                classe: 'indicador-azul',
                secao: 'reunioes',
                analiticoTipo: 'reunioes-status',
                analiticoGrupo: 'AGEND'
            },
            {
                titulo: 'Atas pendentes',
                valor: this.formatarNumero(atasPendentes),
                legenda: `${this.formatarNumero(comProtocolo)} atas com protocolo`,
                icon: 'pi pi-file-edit',
                classe: 'indicador-amarelo',
                secao: 'atas',
                analiticoTipo: 'atas-status',
                analiticoGrupo: 'PEND'
            },
            {
                titulo: 'Convocações pendentes',
                valor: this.formatarNumero(convocacoesPendentes),
                legenda: 'Reuniões com convocação ou confirmação pendente',
                icon: 'pi pi-send',
                classe: 'indicador-vermelho',
                secao: 'convocacoes',
                analiticoTipo: 'pendencias-convocacao',
                analiticoGrupo: 'CONVOCACOES_PENDENTES'
            },
            {
                titulo: 'Atividades pendentes',
                valor: this.formatarNumero(atividadesPendentes),
                legenda: `${this.formatarNumero(this.totalAtividades)} atividades analisadas`,
                icon: 'pi pi-briefcase',
                classe: 'indicador-verde',
                secao: 'atividades',
                analiticoTipo: 'atividades-prazo',
                analiticoGrupo: 'ATRASADAS'
            }
        ];
    }

    private atualizarGraficos(resultado: ResultadoDashboard): void {
        this.reunioesStatusData = this.criarDoughnutData(resultado.reunioesStatus);
        this.reunioesEvolucaoData = this.criarBarraData(resultado.reunioesEvolucao);
        this.reunioesTipoComissaoData = this.criarBarraData(resultado.reunioesTipoComissao);
        this.atasStatusData = this.criarDoughnutData(resultado.atasStatus);
        this.atasProtocoloData = this.criarDoughnutData(resultado.atasProtocolo);
        this.modalidadeData = this.criarDoughnutData(resultado.modalidade);
        this.atosComissaoData = this.criarBarraData(resultado.atosComissao.slice(0, 10));
        this.atosDistribuicaoData = this.criarDoughnutData(resultado.atosDistribuicao);
        this.quorumMedioComissaoData = this.criarBarraData(resultado.quorumMedioComissao.slice(0, 10));
        this.quorumFaixaData = this.criarDoughnutData(resultado.quorumFaixa);
        this.reunioesDuracaoFaixaData = this.criarBarraData(resultado.reunioesDuracaoFaixa);
        this.reunioesDuracaoModalidadeData = this.criarBarraData(resultado.reunioesDuracaoModalidade);
        this.atasLeadTimeFaixaData = this.criarBarraData(resultado.atasLeadTimeFaixa);
        this.atasLeadTimeComissaoData = this.criarBarraData(resultado.atasLeadTimeComissao.slice(0, 10));
        this.reunioesLocalData = this.criarBarraData(resultado.reunioesLocal.slice(0, 10));
        this.reunioesPortalData = this.criarDoughnutData(resultado.reunioesPortal);
        this.atasImportanciaData = this.criarDoughnutData(resultado.atasImportancia);
        this.agendaSemanalData = this.criarBarraData(resultado.agendaSemanal);
        this.atasProducaoMensalData = this.criarBarraData(resultado.atasProducaoMensal);
        this.atividadesEvolucaoData = this.criarBarraData(resultado.atividadesEvolucao);
        this.pendenciasConvocacaoData = this.criarDoughnutData(resultado.pendencias);
        this.atividadesSituacaoData = this.criarBarraData(resultado.atividadesSituacao);
        this.atividadesResponsavelData = this.criarBarraData(resultado.atividadesResponsavel.slice(0, 10));
        this.atividadesPrazoData = this.criarBarraData(resultado.atividadesPrazo);
        this.membrosLgpdData = this.criarDoughnutData(resultado.membrosLgpd);
        this.membrosQuitacaoData = this.criarDoughnutData(resultado.membrosQuitacao);
        this.membrosParticipacaoData = this.criarBarraData(resultado.membrosParticipacao);
        this.membrosMultivinculoData = this.criarBarraData(resultado.membrosMultivinculo);
        this.membrosStatusData = this.criarDoughnutData(resultado.membrosStatus);
        this.membrosUfData = this.criarBarraData(resultado.membrosUf);
        this.membrosEntradasMesData = this.criarBarraData(resultado.membrosEntradasMes);
        this.membrosMandatoTipoData = this.criarBarraData(resultado.membrosMandatoTipo);
        this.membrosStatusHistoricoData = this.criarDoughnutData(resultado.membrosStatusHistorico);
        this.convocacaoPresencasData = this.criarDoughnutData(resultado.convocacaoPresencas);
        this.convocacaoEmailsData = this.criarDoughnutData(resultado.convocacaoEmails);
        this.convocacaoPassagensMesData = this.criarBarraData(resultado.convocacaoPassagensMes);
        this.convocacaoMeioData = this.criarDoughnutData(resultado.convocacaoMeio);
        this.convocacaoSmsStatusData = this.criarDoughnutData(resultado.convocacaoSmsStatus);
        this.convocacaoConvidadosData = this.criarBarraData(resultado.convocacaoConvidados.slice(0, 10));
        this.comissoesTipoData = this.criarBarraData(resultado.comissoesTipo.slice(0, 10));
        this.comissoesStatusData = this.criarDoughnutData(resultado.comissoesStatus);
        this.comissoesSemReuniaoData = this.criarDoughnutData(resultado.comissoesSemReuniao);
        this.comissoesExtintasAnoData = this.criarBarraData(resultado.comissoesExtintasAno);
        this.comissoesVidaMediaTipoData = this.criarBarraData(resultado.comissoesVidaMediaTipo.slice(0, 10));
        this.comissoesEntidadesExternasData = this.criarBarraData(resultado.comissoesEntidadesExternas.slice(0, 10));
        this.comissoesFaixaMembrosData = this.criarBarraData(resultado.comissoesFaixaMembros);
        this.comissoesAntiguidadeData = this.criarBarraData(resultado.comissoesAntiguidade);
        this.atividadesComissaoData = this.criarBarraData(resultado.atividadesComissao.slice(0, 10));
        this.atividadesTempoConclusaoData = this.criarBarraData(resultado.atividadesTempoConclusao);
        this.funilOperacional = this.criarFunilOperacional(resultado.funil);
        this.confirmacoesRanking = this.criarRankingConfirmacoes(resultado.confirmacoes);
        this.confirmacoesData = this.criarConfirmacoesData(this.confirmacoesRanking);
    }

    private criarFiltroReunioes(): ComissaoReuniaoFiltroRequest {
        return {
            tipoPeriodo: EComissaoReuniaoTipoPeriodoFiltro.POR_PERIODO,
            dataInicioReuniao: this.dataInicio || undefined,
            dataFimReuniao: this.dataFim || undefined,
            ufSgUf: this.ufSelecionada ?? undefined,
            excluirStatusReuniaoExcluida: false
        };
    }

    private criarFiltroReunioesComFiltrosAnaliticos(): ComissaoReuniaoFiltroRequest {
        const codigoReuniao = this.numeroFiltro(this.codigoReuniaoFiltro);
        const filtro: ComissaoReuniaoFiltroRequest = {
            tipoPeriodo: this.exibirFiltrosReuniaoAnalitico() && codigoReuniao ? EComissaoReuniaoTipoPeriodoFiltro.CODIGO_REUNIAO : EComissaoReuniaoTipoPeriodoFiltro.POR_PERIODO,
            dataInicioReuniao: this.exibirFiltrosReuniaoAnalitico() && codigoReuniao ? undefined : this.dataInicio || undefined,
            dataFimReuniao: this.exibirFiltrosReuniaoAnalitico() && codigoReuniao ? undefined : this.dataFim || undefined,
            ufSgUf: this.ufSelecionada ?? undefined,
            excluirStatusReuniaoExcluida: false
        };

        if (this.exibirFiltrosReuniaoAnalitico() && codigoReuniao) {
            filtro.codigoReuniao = codigoReuniao;
        }
        if (this.exibirFiltrosReuniaoAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idStatusReuniao', this.idStatusReuniaoFiltro);
            this.definirNumeroFiltro(filtro, 'idLocalReuniao', this.idLocalReuniaoFiltro);
            this.definirNumeroFiltro(filtro, 'idSolicitanteAssistente', this.idSolicitanteAssistenteFiltro);
        }
        if (this.exibirFiltrosComissaoAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idTipoComissaoCt', this.idTipoComissaoCtFiltro);
            this.definirNumeroFiltro(filtro, 'idEstruturaOrganizacional', this.idEstruturaOrganizacionalFiltro);
        }
        if (this.exibirFiltrosAtaAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idStatusAta', this.idStatusAtaFiltro);
        }
        if (this.exibirFiltrosConvocacaoAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idMembroConvidado', this.idMembroConvidadoFiltro);
        }

        if (this.exibirFiltrosConvocacaoAnalitico() && this.tipoConvocacaoFiltro !== 'TODOS') {
            filtro.tipoConvocacao = this.tipoConvocacaoFiltro;
        }
        if (this.exibirFiltrosAtaAnalitico() && this.marcacaoAtaFiltro !== 'TODAS') {
            filtro.marcacaoAta = this.marcacaoAtaFiltro;
        }
        const ufAssistente = this.ufFiltro(this.ufSolicitanteAssistenteFiltro);
        if (this.exibirFiltrosReuniaoAnalitico() && ufAssistente) {
            filtro.ufSolicitanteAssistente = ufAssistente;
        }
        const ufMembro = this.ufFiltro(this.ufMembroConvidadoFiltro);
        if (this.exibirFiltrosConvocacaoAnalitico() && ufMembro) {
            filtro.ufMembroConvidado = ufMembro;
        }

        return filtro;
    }

    private criarFiltroAtividades(): ComissaoAtividadeFiltroRequest {
        return {
            dataInicioAbertura: this.dataInicio || undefined,
            dataFimAbertura: this.dataFim || undefined,
            ufSgUf: this.ufSelecionada ?? undefined
        };
    }

    private criarFiltroAtividadesComFiltrosAnaliticos(): ComissaoAtividadeFiltroRequest {
        const filtro: ComissaoAtividadeFiltroRequest = {
            dataInicioAbertura: this.dataInicio || undefined,
            dataFimAbertura: this.dataFim || undefined,
            dataInicioEncerramento: this.dataInicioEncerramento || undefined,
            dataFimEncerramento: this.dataFimEncerramento || undefined,
            ufSgUf: this.ufSelecionada ?? undefined
        };

        if (this.exibirFiltrosAtividadeAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idSituacaoTarefa', this.idSituacaoTarefaFiltro);
            this.definirNumeroFiltro(filtro, 'idSolicitanteResponsavel', this.idResponsavelAtividadeFiltro);
        }
        if (this.exibirFiltrosComissaoAnalitico()) {
            this.definirNumeroFiltro(filtro, 'idTipoComissaoCt', this.idTipoComissaoCtFiltro);
            this.definirNumeroFiltro(filtro, 'idEstruturaOrganizacional', this.idEstruturaOrganizacionalFiltro);
        }

        const ufResponsavel = this.ufFiltro(this.ufResponsavelAtividadeFiltro);
        if (this.exibirFiltrosAtividadeAnalitico() && ufResponsavel) {
            filtro.ufSolicitanteResponsavel = ufResponsavel;
        }
        const descricao = this.textoFiltro(this.descricaoAtividadeFiltro);
        if (this.exibirFiltrosAtividadeAnalitico() && descricao) {
            filtro.descricaoTarefa = descricao;
        }

        return filtro;
    }

    private carregarOpcoesFiltros(): void {
        forkJoin({
            statusAta: this.reuniaoService.listarStatusAta().pipe(catchError(() => of([] as ComissaoStatusAtaResponse[]))),
            locais: this.reuniaoService.listarLocaisReuniao().pipe(catchError(() => of([] as ComissaoLocalReuniaoResponse[]))),
            tipos: this.reuniaoService.listarTiposComissao().pipe(catchError(() => of([] as ComissaoTipoComissaoResponse[]))),
            situacoes: this.atividadeService.listarSituacoesAtividade().pipe(catchError(() => of([] as ComissaoSituacaoAtividadeResponse[])))
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ statusAta, locais, tipos, situacoes }) => {
                this.opcoesStatusAta = [
                    { label: 'Todos', value: null },
                    ...statusAta.filter((item): item is ComissaoStatusAtaResponse & { idStatusAta: number } => typeof item.idStatusAta === 'number').map((item) => ({ label: item.dsStatusAta || String(item.idStatusAta), value: item.idStatusAta }))
                ];
                this.opcoesLocaisReuniao = [
                    { label: 'Todos', value: null },
                    ...locais
                        .filter((item): item is ComissaoLocalReuniaoResponse & { idLocalReuniao: number } => typeof item.idLocalReuniao === 'number')
                        .map((item) => ({ label: item.dsLocalReuniao || String(item.idLocalReuniao), value: item.idLocalReuniao }))
                ];
                this.opcoesTiposComissao = [
                    { label: 'Todos', value: null },
                    ...tipos
                        .filter((item): item is ComissaoTipoComissaoResponse & { idTipoComissaoCt: number } => typeof item.idTipoComissaoCt === 'number')
                        .map((item) => ({ label: item.dsTipoComissaoCt || String(item.idTipoComissaoCt), value: item.idTipoComissaoCt }))
                ];
                this.opcoesSituacaoAtividade = [
                    { label: 'Todas', value: null },
                    ...situacoes
                        .filter((item): item is ComissaoSituacaoAtividadeResponse & { idSituacaoTarefa: number } => typeof item.idSituacaoTarefa === 'number')
                        .map((item) => ({ label: item.dsSituacaoTarefa || String(item.idSituacaoTarefa), value: item.idSituacaoTarefa }))
                ];
            });
    }

    private definirNumeroFiltro<T extends object>(filtro: T, campo: keyof T, valor: number | string | null | undefined): void {
        const numero = this.numeroFiltro(valor);
        if (numero !== undefined) {
            (filtro as Record<string, unknown>)[String(campo)] = numero;
        }
    }

    private numeroFiltro(valor: number | string | null | undefined): number | undefined {
        if (valor === null || valor === undefined || valor === '') {
            return undefined;
        }
        const numero = Number(valor);
        return Number.isFinite(numero) && numero > 0 ? numero : undefined;
    }

    private textoFiltro(valor: string | null | undefined): string | undefined {
        const texto = valor?.trim();
        return texto || undefined;
    }

    private ufFiltro(valor: string | null | undefined): string | undefined {
        const uf = valor?.trim().toUpperCase();
        return uf && uf.length === 2 ? uf : undefined;
    }

    private criarFunilOperacional(itens: ComissaoEstatisticaFunilResponse[]): ItemFunilComissao[] {
        return itens.map((item) => ({
            label: item.descricao || item.codigo || 'Não informado',
            total: this.numeroSeguro(item.total),
            percentual: Math.min(100, Math.max(0, Math.round(this.numeroSeguro(item.percentual))))
        }));
    }

    private criarRankingConfirmacoes(itens: ComissaoEstatisticaConfirmacaoResponse[]): ConfirmacaoReuniao[] {
        return itens.slice(0, 8).map((item) => {
            const totalConvocacoes = this.numeroSeguro(item.totalConvocacoes);
            const totalConfirmacoes = this.numeroSeguro(item.totalConfirmacoes);
            return {
                label: `#${item.idReuniao ?? '-'} ${item.dsEstruturaOrganizacional ?? 'Comissão'}`.slice(0, 48),
                totalConvocacoes,
                totalConfirmacoes,
                percentual: Math.min(100, Math.max(0, Math.round(this.numeroSeguro(item.percentualConfirmacao))))
            };
        });
    }

    private criarDoughnutData(itens: ComissaoEstatisticaAgrupadaResponse[]): ChartData<'doughnut'> {
        const dados = this.filtrarItensComTotal(itens);
        const mono = this.gerarCoresMonocromaticas(this.obterCorPrimaria(), dados.length);
        // Estados com significado (status, prazo, protocolo...) recebem cor semântica;
        // categorias apenas nominais mantêm a paleta monocromática padrão do sistema.
        const backgroundColors = dados.map((item, indice) => resolverCorSemantica(item.codigo, item.descricao) ?? mono[indice]);
        return {
            labels: dados.map((item) => item.descricao || item.codigo || 'Não informado'),
            datasets: [
                {
                    data: dados.map((item) => this.numeroSeguro(item.total)),
                    backgroundColor: backgroundColors,
                    hoverBackgroundColor: backgroundColors,
                    borderWidth: 1
                }
            ]
        };
    }

    private criarBarraData(itens: ComissaoEstatisticaAgrupadaResponse[]): ChartData<'bar'> {
        const dados = this.filtrarItensComTotal(itens);
        const mono = this.gerarCoresMonocromaticas(this.obterCorPrimaria(), dados.length);
        // Rankings (locais, responsáveis...) não casam termos de estado e seguem a cor primária;
        // barras de estado (prazo, situação) ganham cor semântica por barra.
        const backgroundColor = dados.map((item, indice) => resolverCorSemantica(item.codigo, item.descricao) ?? mono[indice]);
        return {
            labels: dados.map((item) => item.descricao || item.codigo || 'Não informado'),
            datasets: [
                {
                    data: dados.map((item) => this.numeroSeguro(item.total)),
                    backgroundColor,
                    borderRadius: 8
                }
            ]
        };
    }

    private criarConfirmacoesData(itens: ConfirmacaoReuniao[]): ChartData<'bar'> {
        return {
            labels: itens.map((item) => item.label),
            datasets: [
                {
                    data: itens.map((item) => item.percentual),
                    backgroundColor: '#16a34a',
                    borderRadius: 8
                }
            ]
        };
    }

    private criarAnalitico(tipo: AnaliticoGraficoComissaoId): AnaliticoGraficoComissao {
        const configuracoes: Record<AnaliticoGraficoComissaoId, AnaliticoGraficoComissao> = {
            funil: {
                indicador: this.criarIndicadorAnalitico('funil', 'Funil operacional', 'Etapas operacionais de reuniões, convocações, atas e protocolos.', this.totalReunioes),
                colunaUf: 'ETAPA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DETALHE'
            },
            'reunioes-status': {
                indicador: this.criarIndicadorAnalitico('reunioes-status', 'Reuniões por status', 'Distribuição das reuniões por situação operacional.', this.totalReunioes),
                colunaUf: 'STATUS',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'SITUAÇÃO',
                colunaDetalhe: 'DATA'
            },
            'reunioes-evolucao': {
                indicador: this.criarIndicadorAnalitico('reunioes-evolucao', 'Evolução mensal de reuniões', 'Quantidade de reuniões por mês no período filtrado.', this.totalReunioes),
                colunaUf: 'MÊS',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'reunioes-tipo-comissao': {
                indicador: this.criarIndicadorAnalitico('reunioes-tipo-comissao', 'Reuniões por tipo de comissão', 'Distribuição das reuniões por tipo de comissão, câmara ou grupo.', this.totalReunioes),
                colunaUf: 'TIPO',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'atas-status': {
                indicador: this.criarIndicadorAnalitico('atas-status', 'Atas por status', 'Distribuição das atas vinculadas às reuniões filtradas.', this.totalReunioes),
                colunaUf: 'STATUS ATA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS REUNIÃO',
                colunaDetalhe: 'PROTOCOLO'
            },
            'atas-protocolo': {
                indicador: this.criarIndicadorAnalitico('atas-protocolo', 'Atas com e sem protocolo', 'Distribuição das atas conforme vínculo com protocolo.', this.somarTotal(this.dadosAtasProtocolo)),
                colunaUf: 'PROTOCOLO',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS ATA',
                colunaDetalhe: 'NÚMERO'
            },
            modalidade: {
                indicador: this.criarIndicadorAnalitico('modalidade', 'Reuniões por modalidade', 'Distribuição entre reuniões presenciais, virtuais ou híbridas.', this.totalReunioes),
                colunaUf: 'MODALIDADE',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'reunioes-atos-comissao': {
                indicador: this.criarIndicadorAnalitico('reunioes-atos-comissao', 'Atos de concessão por comissão', 'Soma de atos de concessão por comissão no período filtrado.', this.somarTotal(this.dadosAtosComissao)),
                colunaUf: 'COMISSÃO',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'reunioes-atos-distribuicao': {
                indicador: this.criarIndicadorAnalitico('reunioes-atos-distribuicao', 'Atos de concessão: com x sem', 'Reuniões com e sem atos de concessão.', this.somarTotal(this.dadosAtosDistribuicao)),
                colunaUf: 'ATOS',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'quorum-medio-comissao': {
                indicador: this.criarIndicadorAnalitico('quorum-medio-comissao', 'Quórum médio por comissão', 'Top 10 comissões por quórum médio de presença (P/V) nas reuniões.', this.somarTotal(this.dadosQuorumMedioComissao)),
                colunaUf: 'COMISSÃO',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'QUÓRUM'
            },
            'quorum-faixa': {
                indicador: this.criarIndicadorAnalitico('quorum-faixa', 'Reuniões por faixa de quórum', 'Distribuição das reuniões por faixa de quórum de presença.', this.somarTotal(this.dadosQuorumFaixa)),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'QUÓRUM'
            },
            'reunioes-duracao-faixa': {
                indicador: this.criarIndicadorAnalitico('reunioes-duracao-faixa', 'Reuniões por faixa de duração', 'Distribuição das reuniões realizadas por faixa de duração.', this.somarTotal(this.dadosReunioesDuracaoFaixa)),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DURAÇÃO'
            },
            'atas-lead-time-faixa': {
                indicador: this.criarIndicadorAnalitico('atas-lead-time-faixa', 'Atas por faixa de lead time', 'Distribuição das atas publicadas por faixa de lead time de publicação.', this.somarTotal(this.dadosAtasLeadTimeFaixa)),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS ATA',
                colunaDetalhe: 'LEAD TIME (dias)'
            },
            'reunioes-local': {
                indicador: this.criarIndicadorAnalitico('reunioes-local', 'Reuniões por local', 'Distribuição das reuniões por local de realização.', this.totalReunioes),
                colunaUf: 'LOCAL',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'reunioes-portal': {
                indicador: this.criarIndicadorAnalitico('reunioes-portal', 'Reuniões visíveis no portal', 'Distribuição das reuniões conforme visibilidade no portal.', this.totalReunioes),
                colunaUf: 'PORTAL',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'atas-importancia': {
                indicador: this.criarIndicadorAnalitico('atas-importancia', 'Atas importantes', 'Distribuição das atas marcadas como importantes.', this.somarTotal(this.dadosAtasImportancia)),
                colunaUf: 'IMPORTÂNCIA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS ATA',
                colunaDetalhe: 'DATA'
            },
            'agenda-semanal': {
                indicador: this.criarIndicadorAnalitico('agenda-semanal', 'Agenda futura por semana', 'Reuniões agendadas ou remarcadas para as próximas semanas.', this.somarTotal(this.dadosAgendaSemanal)),
                colunaUf: 'SEMANA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'DATA'
            },
            'atas-producao-mensal': {
                indicador: this.criarIndicadorAnalitico('atas-producao-mensal', 'Produção mensal de atas', 'Atas preenchidas por mês no período filtrado.', this.somarTotal(this.dadosAtasProducaoMensal)),
                colunaUf: 'MÊS',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS ATA',
                colunaDetalhe: 'PROTOCOLO'
            },
            'atividades-evolucao': {
                indicador: this.criarIndicadorAnalitico('atividades-evolucao', 'Evolução mensal de atividades', 'Atividades abertas por mês no período filtrado.', this.somarTotal(this.dadosAtividadesEvolucao)),
                colunaUf: 'MÊS',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'RESPONSÁVEL',
                colunaDetalhe: 'DETALHE'
            },
            'pendencias-convocacao': {
                indicador: this.criarIndicadorAnalitico('pendencias-convocacao', 'Pendências de convocação', 'Reuniões com convocação ou confirmação pendente.', this.somarTotal(this.dadosPendenciasConvocacao)),
                colunaUf: 'PENDÊNCIA',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'STATUS',
                colunaDetalhe: 'CONVOCAÇÕES'
            },
            'atividades-situacao': {
                indicador: this.criarIndicadorAnalitico('atividades-situacao', 'Atividades por situação', 'Atividades vinculadas ao módulo por situação atual.', this.totalAtividades),
                colunaUf: 'SITUAÇÃO',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'RESPONSÁVEL',
                colunaDetalhe: 'DETALHE'
            },
            'atividades-responsavel': {
                indicador: this.criarIndicadorAnalitico('atividades-responsavel', 'Atividades por responsável', 'Atividades vinculadas ao módulo agrupadas por responsável.', this.somarTotal(this.dadosAtividadesResponsavel)),
                colunaUf: 'RESPONSÁVEL',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'SITUAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'atividades-prazo': {
                indicador: this.criarIndicadorAnalitico('atividades-prazo', 'Atividades por prazo', 'Atividades atrasadas, no prazo ou concluídas.', this.somarTotal(this.dadosAtividadesPrazo)),
                colunaUf: 'PRAZO',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'RESPONSÁVEL',
                colunaDetalhe: 'DETALHE'
            },
            confirmacoes: {
                indicador: this.criarIndicadorAnalitico('confirmacoes', 'Taxa de confirmação por reunião', 'Reuniões com convocação ordenadas por percentual de confirmação.', this.confirmacoesRanking.length),
                colunaUf: 'CONFIRMAÇÃO',
                colunaIdentificador: 'REUNIÃO',
                colunaNome: 'COMISSÃO',
                colunaSituacao: 'TAXA',
                colunaDetalhe: 'CONFIRMAÇÕES'
            },
            'membros-lgpd': {
                indicador: this.criarIndicadorAnalitico('membros-lgpd', 'Membros por status LGPD', 'Distribuição dos membros ativos conforme aceite da LGPD.', this.somarTotal(this.dadosMembrosLgpd)),
                colunaUf: 'STATUS LGPD',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'membros-quitacao': {
                indicador: this.criarIndicadorAnalitico('membros-quitacao', 'Membros por quitação de débitos', 'Distribuição dos membros ativos conforme quitação financeira.', this.somarTotal(this.dadosMembrosQuitacao)),
                colunaUf: 'STATUS FINANCEIRO',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'membros-participacao': {
                indicador: this.criarIndicadorAnalitico('membros-participacao', 'Membros por participação', 'Distribuição dos membros ativos por tipo de participação.', this.somarTotal(this.dadosMembrosParticipacao)),
                colunaUf: 'PARTICIPAÇÃO',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'membros-multivinculo': {
                indicador: this.criarIndicadorAnalitico('membros-multivinculo', 'Membros por nº de comissões', 'Distribuição dos membros ativos por quantidade de comissões vigentes.', this.somarTotal(this.dadosMembrosMultivinculo)),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'COMISSÕES'
            },
            'membros-status': {
                indicador: this.criarIndicadorAnalitico('membros-status', 'Membros ativos x afastados', 'Distribuição dos membros entre ativos e afastados.', this.somarTotal(this.dadosMembrosStatus)),
                colunaUf: 'STATUS',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'membros-uf': {
                indicador: this.criarIndicadorAnalitico('membros-uf', 'Membros por UF', 'Distribuição dos membros ativos por unidade da federação.', this.somarTotal(this.dadosMembrosUf)),
                colunaUf: 'UF',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'DETALHE'
            },
            'convocacoes-presencas': {
                indicador: this.criarIndicadorAnalitico('convocacoes-presencas', 'Presenças por tipo', 'Distribuição das presenças registradas nas reuniões filtradas.', this.somarTotal(this.dadosConvocacaoPresencas)),
                colunaUf: 'PRESENÇA',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'COMISSÃO',
                colunaDetalhe: 'REUNIÃO'
            },
            'convocacoes-emails': {
                indicador: this.criarIndicadorAnalitico('convocacoes-emails', 'Envio de e-mails de convocação', 'Distribuição dos e-mails de convocação enviados e não enviados.', this.somarTotal(this.dadosConvocacaoEmails)),
                colunaUf: 'STATUS',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'COMISSÃO',
                colunaDetalhe: 'REUNIÃO'
            },
            'convocacoes-meio': {
                indicador: this.criarIndicadorAnalitico('convocacoes-meio', 'Confirmações por meio', 'Distribuição das confirmações registradas por meio de confirmação.', this.somarTotal(this.dadosConvocacaoMeio)),
                colunaUf: 'MEIO',
                colunaIdentificador: 'CRM',
                colunaNome: 'MEMBRO',
                colunaSituacao: 'COMISSÃO',
                colunaDetalhe: 'REUNIÃO'
            },
            'convocacoes-sms-status': {
                indicador: this.criarIndicadorAnalitico('convocacoes-sms-status', 'SMS de convocação por status de envio', 'Distribuição dos SMS de convocação enviados por status de envio.', this.somarTotal(this.dadosConvocacaoSmsStatus)),
                colunaUf: 'STATUS SMS',
                colunaIdentificador: 'CRM',
                colunaNome: 'DESTINATÁRIO',
                colunaSituacao: 'COMISSÃO',
                colunaDetalhe: 'REUNIÃO'
            },
            'convocacoes-convidados': {
                indicador: this.criarIndicadorAnalitico(
                    'convocacoes-convidados',
                    'Convidados mais frequentes',
                    'Ranking das pessoas presentes que não eram membros vigentes da comissão na data da reunião.',
                    this.somarTotal(this.dadosConvocacaoConvidados)
                ),
                colunaUf: 'CONVIDADO',
                colunaIdentificador: 'CRM',
                colunaNome: 'PESSOA',
                colunaSituacao: 'COMISSÃO',
                colunaDetalhe: 'REUNIÃO'
            },
            'comissoes-tipo': {
                indicador: this.criarIndicadorAnalitico('comissoes-tipo', 'Comissões por tipo', 'Distribuição das comissões por tipo, câmara ou grupo.', this.somarTotal(this.dadosComissoesTipo)),
                colunaUf: 'TIPO',
                colunaIdentificador: 'ESTRUTURA',
                colunaNome: 'COMISSÃO',
                colunaSituacao: '',
                colunaDetalhe: null
            },
            'comissoes-status': {
                indicador: this.criarIndicadorAnalitico('comissoes-status', 'Comissões ativas x inativas', 'Distribuição das comissões entre ativas e inativas/extintas.', this.somarTotal(this.dadosComissoesStatus)),
                colunaUf: 'STATUS',
                colunaIdentificador: 'ESTRUTURA',
                colunaNome: 'COMISSÃO',
                colunaSituacao: '',
                colunaDetalhe: null
            },
            'comissoes-sem-reuniao': {
                indicador: this.criarIndicadorAnalitico('comissoes-sem-reuniao', 'Comissões sem reunião futura', 'Comissões ativas com e sem reunião agendada futura.', this.somarTotal(this.dadosComissoesSemReuniao)),
                colunaUf: 'AGENDA',
                colunaIdentificador: 'ESTRUTURA',
                colunaNome: 'COMISSÃO',
                colunaSituacao: '',
                colunaDetalhe: null
            },
            'comissoes-extintas-ano': {
                indicador: this.criarIndicadorAnalitico('comissoes-extintas-ano', 'Comissões extintas por ano', 'Quantidade de comissões extintas, agrupada por ano.', this.somarTotal(this.dadosComissoesExtintasAno)),
                colunaUf: 'ANO',
                colunaIdentificador: 'ESTRUTURA',
                colunaNome: 'COMISSÃO',
                colunaSituacao: '',
                colunaDetalhe: null
            },
            'comissoes-entidades-externas': {
                indicador: this.criarIndicadorAnalitico(
                    'comissoes-entidades-externas',
                    'Entidades externas por representantes',
                    'Ranking das entidades externas por número de representantes vigentes.',
                    this.somarTotal(this.dadosComissoesEntidadesExternas)
                ),
                colunaUf: 'ENTIDADE',
                colunaIdentificador: 'CRM',
                colunaNome: 'REPRESENTANTE',
                colunaSituacao: 'PARTICIPAÇÃO',
                colunaDetalhe: 'COMISSÃO'
            },
            'comissoes-faixa-membros': {
                indicador: this.criarIndicadorAnalitico(
                    'comissoes-faixa-membros',
                    'Comissões por faixa de nº de membros',
                    'Distribuição das comissões vigentes por faixa de número de membros vigentes.',
                    this.somarTotal(this.dadosComissoesFaixaMembros)
                ),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'ESTRUTURA',
                colunaNome: 'COMISSÃO',
                colunaSituacao: '',
                colunaDetalhe: null
            },
            'atividades-comissao': {
                indicador: this.criarIndicadorAnalitico('atividades-comissao', 'Atividades por comissão', 'Distribuição das atividades por comissão no período filtrado.', this.somarTotal(this.dadosAtividadesComissao)),
                colunaUf: 'COMISSÃO',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'RESPONSÁVEL',
                colunaDetalhe: 'DETALHE'
            },
            'atividades-tempo-conclusao': {
                indicador: this.criarIndicadorAnalitico('atividades-tempo-conclusao', 'Tarefas por faixa de tempo de conclusão', 'Tarefas concluídas agrupadas pelo tempo (dias) entre início e término.', this.totalAtividadesTempoConclusao),
                colunaUf: 'FAIXA',
                colunaIdentificador: 'ATIVIDADE',
                colunaNome: 'DESCRIÇÃO',
                colunaSituacao: 'RESPONSÁVEL',
                colunaDetalhe: 'DETALHE'
            }
        };

        return {
            ...configuracoes[tipo]
        };
    }

    private criarIndicadorAnalitico(id: string, titulo: string, descricao: string, quantidade: number): IndicadorPendencia {
        return {
            id,
            titulo,
            descricao,
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            quantidade
        };
    }

    private atualizarAnaliticoSelecionado(): void {
        if (!this.tipoAnaliticoSelecionado) {
            return;
        }

        const opcoes = this.obterOpcoesGrupoAnalitico(this.tipoAnaliticoSelecionado);
        const codigoAindaValido = opcoes.some((opcao) => opcao.codigo === this.grupoAnaliticoCodigo);
        this.grupoAnaliticoCodigo = codigoAindaValido ? this.grupoAnaliticoCodigo : (opcoes[0]?.codigo ?? null);
        this.analiticoSelecionado = this.criarAnalitico(this.tipoAnaliticoSelecionado);
        this.carregarAnalitico();
    }

    private carregarAnalitico(): void {
        if (!this.tipoAnaliticoSelecionado) {
            return;
        }

        const rows = this.rowsAnalitico || 100;
        const page = Math.floor((this.firstAnalitico || 0) / rows);
        this.carregandoAnalitico = true;

        this.consultarAnalitico(this.tipoAnaliticoSelecionado, page, rows)
            .pipe(
                catchError(() => of({ content: [], totalElements: 0 } as ComissaoEstatisticaAnaliticoPageResponse)),
                finalize(() => {
                    this.carregandoAnalitico = false;
                    this.cdr.markForCheck();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((resultado) => {
                const content = resultado.content ?? [];
                this.linhasAnalitico = content.map((item) => this.mapLinhaAnalitico(this.tipoAnaliticoSelecionado!, item));
                this.totalRegistrosAnalitico = resultado.totalElements ?? this.linhasAnalitico.length;
                this.cdr.markForCheck();
            });
    }

    /** Carrega todas as páginas do analítico atualmente selecionado, para exportação completa em CSV. */
    private carregarTodasLinhasAnalitico(): Observable<PendenciaLinha[]> {
        const tipo = this.tipoAnaliticoSelecionado;
        if (!tipo) {
            return of([]);
        }

        const tamanhoPagina = 500;
        const maximoRegistros = 5000;
        return this.consultarAnalitico(tipo, 0, tamanhoPagina).pipe(
            expand((resposta, indice) => {
                const total = resposta.totalElements ?? 0;
                const carregados = (indice + 1) * tamanhoPagina;
                return carregados < total && carregados < maximoRegistros ? this.consultarAnalitico(tipo, indice + 1, tamanhoPagina) : EMPTY;
            }),
            map((resposta) => (resposta.content ?? []).map((item) => this.mapLinhaAnalitico(tipo, item))),
            reduce((acumulado, linhas) => acumulado.concat(linhas), [] as PendenciaLinha[])
        );
    }

    private consultarAnalitico(tipo: AnaliticoGraficoComissaoId, page: number, size: number): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        switch (tipo) {
            case 'funil':
                return this.estatisticaService.consultarAnaliticoFunilOperacional(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-status':
                return this.estatisticaService.consultarAnaliticoReunioesPorStatus(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-evolucao':
                return this.estatisticaService.consultarAnaliticoReunioesEvolucaoMensal(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-tipo-comissao':
                return this.estatisticaService.consultarAnaliticoReunioesPorTipoComissao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atas-status':
                return this.estatisticaService.consultarAnaliticoAtasPorStatus(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atas-protocolo':
                return this.estatisticaService.consultarAnaliticoAtasPorProtocolo(this.criarFiltroReunioesAnalitico(), page, size);
            case 'modalidade':
                return this.estatisticaService.consultarAnaliticoReunioesPorModalidade(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-atos-comissao':
                return this.estatisticaService.consultarAnaliticoAtosPorComissao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-atos-distribuicao':
                return this.estatisticaService.consultarAnaliticoAtosDistribuicao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'quorum-medio-comissao':
                return this.estatisticaService.consultarAnaliticoQuorumMedioComissao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'quorum-faixa':
                return this.estatisticaService.consultarAnaliticoQuorumFaixa(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-duracao-faixa':
                return this.estatisticaService.consultarAnaliticoReunioesDuracaoFaixa(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atas-lead-time-faixa':
                return this.estatisticaService.consultarAnaliticoAtasLeadTimeFaixa(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-local':
                return this.estatisticaService.consultarAnaliticoReunioesPorLocal(this.criarFiltroReunioesAnalitico(), page, size);
            case 'reunioes-portal':
                return this.estatisticaService.consultarAnaliticoReunioesPorVisibilidadePortal(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atas-importancia':
                return this.estatisticaService.consultarAnaliticoAtasPorImportancia(this.criarFiltroReunioesAnalitico(), page, size);
            case 'agenda-semanal':
                return this.estatisticaService.consultarAnaliticoReunioesAgendaSemanal(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atas-producao-mensal':
                return this.estatisticaService.consultarAnaliticoAtasProducaoMensal(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atividades-evolucao':
                return this.estatisticaService.consultarAnaliticoAtividadesEvolucaoMensal(this.criarFiltroAtividadesAnalitico(), page, size);
            case 'pendencias-convocacao':
                return this.estatisticaService.consultarAnaliticoPendenciasConvocacao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atividades-situacao':
                return this.estatisticaService.consultarAnaliticoAtividadesPorSituacao(this.criarFiltroAtividadesAnalitico(), page, size);
            case 'atividades-responsavel':
                return this.estatisticaService.consultarAnaliticoAtividadesPorResponsavel(this.criarFiltroAtividadesAnalitico(), page, size);
            case 'atividades-prazo':
                return this.estatisticaService.consultarAnaliticoAtividadesPorPrazo(this.criarFiltroAtividadesAnalitico(), page, size);
            case 'confirmacoes':
                return this.estatisticaService.consultarAnaliticoConfirmacoesPorReuniao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-lgpd':
                return this.estatisticaService.consultarAnaliticoMembrosLgpd(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-quitacao':
                return this.estatisticaService.consultarAnaliticoMembrosQuitacao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-participacao':
                return this.estatisticaService.consultarAnaliticoMembrosPorParticipacao(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-multivinculo':
                return this.estatisticaService.consultarAnaliticoMembrosMultivinculo(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-status':
                return this.estatisticaService.consultarAnaliticoMembrosPorStatus(this.criarFiltroReunioesAnalitico(), page, size);
            case 'membros-uf':
                return this.estatisticaService.consultarAnaliticoMembrosPorUf(this.criarFiltroReunioesAnalitico(), page, size);
            case 'convocacoes-presencas':
                return this.estatisticaService.consultarAnaliticoConvocacaoPresencas(this.criarFiltroReunioesAnalitico(), page, size);
            case 'convocacoes-emails':
                return this.estatisticaService.consultarAnaliticoConvocacaoEmails(this.criarFiltroReunioesAnalitico(), page, size);
            case 'convocacoes-meio':
                return this.estatisticaService.consultarAnaliticoConvocacoesMeio(this.criarFiltroReunioesAnalitico(), page, size);
            case 'convocacoes-sms-status':
                return this.estatisticaService.consultarAnaliticoConvocacoesSmsStatus(this.criarFiltroReunioesAnalitico(), page, size);
            case 'convocacoes-convidados':
                return this.estatisticaService.consultarAnaliticoConvocacoesConvidados(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-tipo':
                return this.estatisticaService.consultarAnaliticoComissoesPorTipo(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-status':
                return this.estatisticaService.consultarAnaliticoComissoesPorStatus(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-sem-reuniao':
                return this.estatisticaService.consultarAnaliticoComissoesSemReuniaoFutura(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-extintas-ano':
                return this.estatisticaService.consultarAnaliticoComissoesExtintasAno(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-entidades-externas':
                return this.estatisticaService.consultarAnaliticoComissoesEntidadesExternas(this.criarFiltroReunioesAnalitico(), page, size);
            case 'comissoes-faixa-membros':
                return this.estatisticaService.consultarAnaliticoComissoesFaixaMembros(this.criarFiltroReunioesAnalitico(), page, size);
            case 'atividades-comissao':
                return this.estatisticaService.consultarAnaliticoAtividadesPorComissao(this.criarFiltroAtividadesAnalitico(), page, size);
            case 'atividades-tempo-conclusao':
                return this.estatisticaService.consultarAnaliticoAtividadesPorTempoConclusao(this.criarFiltroAtividadesAnalitico(), page, size);
        }
    }

    private criarFiltroReunioesAnalitico(): ComissaoReuniaoFiltroRequest & { codigoAgrupamento?: string } {
        const filtro: ComissaoReuniaoFiltroRequest & { codigoAgrupamento?: string } = {
            ...this.criarFiltroReunioesComFiltrosAnaliticos(),
            codigoAgrupamento: this.grupoAnaliticoCodigo ?? undefined
        };

        if (this.tipoAnaliticoSelecionado === 'confirmacoes' && this.grupoAnaliticoCodigo) {
            const codigoReuniao = Number(this.grupoAnaliticoCodigo);
            if (Number.isFinite(codigoReuniao)) {
                filtro.tipoPeriodo = EComissaoReuniaoTipoPeriodoFiltro.CODIGO_REUNIAO;
                filtro.codigoReuniao = codigoReuniao;
            }
        }

        return filtro;
    }

    private criarFiltroAtividadesAnalitico(): ComissaoAtividadeFiltroRequest & { codigoAgrupamento?: string } {
        return {
            ...this.criarFiltroAtividadesComFiltrosAnaliticos(),
            codigoAgrupamento: this.grupoAnaliticoCodigo ?? undefined
        };
    }

    private obterFonteOpcoesAnalitico(tipo: AnaliticoGraficoComissaoId): ComissaoEstatisticaAgrupadaResponse[] {
        switch (tipo) {
            case 'funil':
                return this.dadosFunil;
            case 'reunioes-status':
                return this.dadosReunioesStatus;
            case 'reunioes-evolucao':
                return this.dadosReunioesEvolucao;
            case 'reunioes-tipo-comissao':
                return this.dadosReunioesTipoComissao;
            case 'atas-status':
                return this.dadosAtasStatus;
            case 'atas-protocolo':
                return this.dadosAtasProtocolo;
            case 'modalidade':
                return this.dadosModalidade;
            case 'reunioes-atos-comissao':
                return this.dadosAtosComissao;
            case 'reunioes-atos-distribuicao':
                return this.dadosAtosDistribuicao;
            case 'quorum-medio-comissao':
                return this.dadosQuorumMedioComissao;
            case 'quorum-faixa':
                return this.dadosQuorumFaixa;
            case 'reunioes-duracao-faixa':
                return this.dadosReunioesDuracaoFaixa;
            case 'atas-lead-time-faixa':
                return this.dadosAtasLeadTimeFaixa;
            case 'reunioes-local':
                return this.dadosReunioesLocal;
            case 'reunioes-portal':
                return this.dadosReunioesPortal;
            case 'atas-importancia':
                return this.dadosAtasImportancia;
            case 'agenda-semanal':
                return this.dadosAgendaSemanal;
            case 'atas-producao-mensal':
                return this.dadosAtasProducaoMensal;
            case 'atividades-evolucao':
                return this.dadosAtividadesEvolucao;
            case 'pendencias-convocacao':
                return this.dadosPendenciasConvocacao;
            case 'atividades-situacao':
                return this.dadosAtividadesSituacao;
            case 'atividades-responsavel':
                return this.dadosAtividadesResponsavel;
            case 'atividades-prazo':
                return this.dadosAtividadesPrazo;
            case 'membros-lgpd':
                return this.dadosMembrosLgpd;
            case 'membros-quitacao':
                return this.dadosMembrosQuitacao;
            case 'membros-participacao':
                return this.dadosMembrosParticipacao;
            case 'membros-multivinculo':
                return this.dadosMembrosMultivinculo;
            case 'membros-status':
                return this.dadosMembrosStatus;
            case 'membros-uf':
                return this.dadosMembrosUf;
            case 'convocacoes-presencas':
                return this.dadosConvocacaoPresencas;
            case 'convocacoes-emails':
                return this.dadosConvocacaoEmails;
            case 'convocacoes-meio':
                return this.dadosConvocacaoMeio;
            case 'convocacoes-sms-status':
                return this.dadosConvocacaoSmsStatus;
            case 'convocacoes-convidados':
                return this.dadosConvocacaoConvidados;
            case 'comissoes-tipo':
                return this.dadosComissoesTipo;
            case 'comissoes-status':
                return this.dadosComissoesStatus;
            case 'comissoes-sem-reuniao':
                return this.dadosComissoesSemReuniao;
            case 'comissoes-extintas-ano':
                return this.dadosComissoesExtintasAno;
            case 'comissoes-entidades-externas':
                return this.dadosComissoesEntidadesExternas;
            case 'comissoes-faixa-membros':
                return this.dadosComissoesFaixaMembros;
            case 'atividades-comissao':
                return this.dadosAtividadesComissao;
            case 'atividades-tempo-conclusao':
                return this.dadosAtividadesTempoConclusao;
            case 'confirmacoes':
                return [];
        }
    }

    private obterGrupoAnaliticoSelecionado(): ComissaoEstatisticaAgrupadaResponse | null {
        return this.obterOpcoesGrupoAnalitico().find((item) => item.codigo === this.grupoAnaliticoCodigo) ?? null;
    }

    private mapLinhaAnalitico(tipo: AnaliticoGraficoComissaoId, item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        if (tipo === 'atividades-situacao' || tipo === 'atividades-responsavel' || tipo === 'atividades-prazo' || tipo === 'atividades-comissao' || tipo === 'atividades-evolucao' || tipo === 'atividades-tempo-conclusao') {
            return this.mapLinhaAtividadeAnalitico(item);
        }
        if (tipo === 'membros-lgpd' || tipo === 'membros-quitacao' || tipo === 'membros-participacao' || tipo === 'membros-multivinculo' || tipo === 'membros-status' || tipo === 'membros-uf' || tipo === 'comissoes-entidades-externas') {
            return this.mapLinhaMembroAnalitico(tipo, item);
        }
        if (tipo === 'convocacoes-presencas' || tipo === 'convocacoes-emails' || tipo === 'convocacoes-meio' || tipo === 'convocacoes-sms-status' || tipo === 'convocacoes-convidados') {
            return this.mapLinhaConvocacaoAnalitico(item);
        }
        if (tipo === 'comissoes-tipo' || tipo === 'comissoes-status' || tipo === 'comissoes-sem-reuniao' || tipo === 'comissoes-extintas-ano' || tipo === 'comissoes-faixa-membros') {
            return this.mapLinhaComissaoAnalitico(item);
        }

        return this.mapLinhaReuniaoAnalitico(tipo, item);
    }

    private mapLinhaMembroAnalitico(tipo: AnaliticoGraficoComissaoId, item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        const codigoSituacao = tipo === 'membros-lgpd' ? (item.assinouLgpd ? 'A' : 'C') : tipo === 'membros-quitacao' ? (item.devedor ? 'C' : 'A') : (item.grupoCodigo ?? '');

        return {
            uf: item.grupoDescricao || item.grupoCodigo || '-',
            crm: item.idSolicitanteMembro ? String(item.idSolicitanteMembro) : '-',
            nome: item.nmSolicitanteMembro || 'Membro',
            situacao: item.dsParticipacaoMembro || '-',
            codigoSituacao: codigoSituacao,
            detalhe: [item.dsEstruturaOrganizacional, item.sgUfMembro].filter(Boolean).join(' | ')
        };
    }

    private mapLinhaConvocacaoAnalitico(item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        return {
            uf: item.grupoDescricao || item.grupoCodigo || '-',
            crm: item.idSolicitanteMembro ? String(item.idSolicitanteMembro) : '-',
            nome: item.nmSolicitanteMembro || 'Membro',
            situacao: item.dsEstruturaOrganizacional || 'Comissão',
            codigoSituacao: item.grupoCodigo ?? '',
            detalhe: [item.idReuniao ? `#${item.idReuniao}` : null, this.formatarDataHora(item.dhInicioReuniao)].filter((valor) => valor && valor !== '-').join(' | ') || '-'
        };
    }

    private mapLinhaComissaoAnalitico(item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        return {
            uf: item.grupoDescricao || item.grupoCodigo || '-',
            crm: item.idEstruturaOrganizacional ? `#${item.idEstruturaOrganizacional}` : '-',
            nome: item.dsEstruturaOrganizacional || 'Comissão',
            situacao: '-',
            codigoSituacao: item.grupoCodigo ?? '',
            detalhe: '-'
        };
    }

    private mapLinhaReuniaoAnalitico(tipo: AnaliticoGraficoComissaoId, item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        const grupo = item.grupoDescricao || item.grupoCodigo || '-';
        const statusReuniao = item.dsStatusReuniao || '-';
        const percentualConfirmacao = `${this.numeroSeguro(item.percentualConfirmacao).toLocaleString('pt-BR')}%`;

        return {
            uf: grupo,
            crm: item.idReuniao ? `#${item.idReuniao}` : '-',
            nome: item.dsEstruturaOrganizacional || 'Comissão',
            situacao:
                tipo === 'atas-protocolo' || tipo === 'atas-importancia' || tipo === 'atas-producao-mensal' || tipo === 'atas-lead-time-faixa'
                    ? item.dsStatusAta || 'Sem ata'
                    : this.obterSituacaoReuniaoAnalitico(tipo, statusReuniao, percentualConfirmacao),
            codigoSituacao: String(item.idStatusReuniao ?? item.idStatusAta ?? item.grupoCodigo ?? ''),
            detalhe: this.obterDetalheReuniaoAnalitico(tipo, item)
        };
    }

    private mapLinhaAtividadeAnalitico(item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
        const responsavel = [item.nmSolicitanteResponsavel, item.sgUfSolicitanteResponsavel].filter(Boolean).join(' / ');
        const detalhe = [item.dsEstruturaOrganizacional, item.dsSituacaoTarefa, this.criarPeriodoAnalitico(item.dtInicio, item.dtTermino)].filter(Boolean).join(' | ');

        return {
            uf: item.grupoDescricao || item.grupoCodigo || '-',
            crm: item.codigoTarefa || (item.idTarefa ? `#${item.idTarefa}` : '-'),
            nome: item.dsTarefa || 'Atividade',
            situacao: responsavel || '-',
            codigoSituacao: String(item.idSituacaoTarefa ?? item.grupoCodigo ?? ''),
            detalhe: detalhe || '-'
        };
    }

    private obterSituacaoReuniaoAnalitico(tipo: AnaliticoGraficoComissaoId, statusReuniao: string, percentualConfirmacao: string): string {
        switch (tipo) {
            case 'atas-status':
                return statusReuniao;
            case 'atas-protocolo':
                return statusReuniao;
            case 'modalidade':
                return statusReuniao;
            case 'reunioes-atos-comissao':
                return statusReuniao;
            case 'reunioes-atos-distribuicao':
                return statusReuniao;
            case 'quorum-medio-comissao':
                return statusReuniao;
            case 'quorum-faixa':
                return statusReuniao;
            case 'reunioes-duracao-faixa':
                return statusReuniao;
            case 'atas-lead-time-faixa':
                return statusReuniao;
            case 'reunioes-local':
                return statusReuniao;
            case 'reunioes-portal':
                return statusReuniao;
            case 'atas-importancia':
                return statusReuniao;
            case 'agenda-semanal':
                return statusReuniao;
            case 'atas-producao-mensal':
                return statusReuniao;
            case 'atividades-evolucao':
                return '-';
            case 'pendencias-convocacao':
                return statusReuniao;
            case 'confirmacoes':
                return percentualConfirmacao;
            case 'funil':
                return statusReuniao;
            case 'reunioes-status':
                return statusReuniao;
            case 'reunioes-evolucao':
                return statusReuniao;
            case 'reunioes-tipo-comissao':
                return statusReuniao;
            case 'atividades-situacao':
                return '-';
            case 'atividades-responsavel':
                return '-';
            case 'atividades-prazo':
                return '-';
            case 'atividades-comissao':
            case 'membros-lgpd':
            case 'membros-quitacao':
            case 'membros-participacao':
            case 'membros-multivinculo':
            case 'membros-status':
            case 'membros-uf':
            case 'convocacoes-presencas':
            case 'convocacoes-emails':
            case 'convocacoes-meio':
            case 'convocacoes-sms-status':
            case 'convocacoes-convidados':
            case 'comissoes-tipo':
            case 'comissoes-status':
            case 'comissoes-sem-reuniao':
            case 'comissoes-extintas-ano':
            case 'comissoes-entidades-externas':
            case 'comissoes-faixa-membros':
            case 'atividades-tempo-conclusao':
                return '-';
        }
    }

    private obterDetalheReuniaoAnalitico(tipo: AnaliticoGraficoComissaoId, item: ComissaoEstatisticaAnaliticoResponse): string {
        if (tipo === 'atas-status' || tipo === 'atas-protocolo' || tipo === 'atas-producao-mensal') {
            return item.numeroProtocolo || (item.idProtocolo ? `Protocolo ${item.idProtocolo}` : '-');
        }

        if (tipo === 'pendencias-convocacao') {
            return `${this.formatarNumero(item.totalConvocacoes)} conv. / ${this.formatarNumero(item.totalConfirmacoes)} conf. / ${this.formatarNumero(item.totalMembros)} membros`;
        }

        if (tipo === 'confirmacoes') {
            return `${this.formatarNumero(item.totalConfirmacoes)} de ${this.formatarNumero(item.totalConvocacoes)} confirmações`;
        }

        if (tipo === 'quorum-medio-comissao' || tipo === 'quorum-faixa') {
            return `${this.numeroSeguro(item.percentualQuorum).toLocaleString('pt-BR')}%`;
        }

        if (tipo === 'reunioes-duracao-faixa') {
            return `${this.numeroSeguro(item.duracaoMinutos).toLocaleString('pt-BR')} min`;
        }

        if (tipo === 'atas-lead-time-faixa') {
            return `${this.numeroSeguro(item.leadTimeDias).toLocaleString('pt-BR')} dias`;
        }

        return this.formatarDataHora(item.dhInicioReuniao);
    }

    private criarPeriodoAnalitico(dataInicio?: string | null, dataFim?: string | null): string {
        const inicio = this.formatarDataAnalitica(dataInicio);
        const fim = this.formatarDataAnalitica(dataFim);
        return [inicio, fim].filter((valor) => valor && valor !== '-').join(' a ');
    }

    private formatarDataAnalitica(valor?: string | null): string {
        if (!valor) {
            return '-';
        }

        const [data] = valor.split('T');
        const [ano, mes, dia] = data.split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : this.formatarDataHora(valor);
    }

    private criarDoughnutVazio(): ChartData<'doughnut'> {
        return { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    }

    private criarBarraVazia(): ChartData<'bar'> {
        return { labels: [], datasets: [{ data: [], backgroundColor: this.obterCorPrimaria() }] };
    }

    private filtrarItensComTotal(itens: ComissaoEstatisticaAgrupadaResponse[]): ComissaoEstatisticaAgrupadaResponse[] {
        return itens.filter((item) => this.numeroSeguro(item.total) > 0);
    }

    private somarTotal(itens: ComissaoEstatisticaAgrupadaResponse[]): number {
        return itens.reduce((total, item) => total + this.numeroSeguro(item.total), 0);
    }

    private obterMaiorTotalMembrosUf(): number {
        return this.obterMembrosUfItens().reduce((maior, item) => Math.max(maior, this.numeroSeguro(item.total)), 0);
    }

    private buscarTotal(itens: ComissaoEstatisticaAgrupadaResponse[], texto: string): number {
        return itens.filter((item) => this.contemTexto(item.codigo, texto) || this.contemTexto(item.descricao, texto)).reduce((total, item) => total + this.numeroSeguro(item.total), 0);
    }

    private buscarTotalPorCodigo(itens: ComissaoEstatisticaAgrupadaResponse[], codigo: string): number {
        return this.numeroSeguro(itens.find((item) => this.normalizarTexto(item.codigo) === this.normalizarTexto(codigo))?.total);
    }

    private somarPorDescricao(itens: ComissaoEstatisticaAgrupadaResponse[], texto: string): number {
        return itens.filter((item) => this.contemTexto(item.descricao, texto)).reduce((total, item) => total + this.numeroSeguro(item.total), 0);
    }

    private contemTexto(valor: string | null | undefined, texto: string): boolean {
        return this.normalizarTexto(valor).includes(this.normalizarTexto(texto));
    }

    private normalizarTexto(valor: string | null | undefined): string {
        return (valor ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();
    }

    private numeroSeguro(valor: number | null | undefined): number {
        return Number.isFinite(Number(valor)) ? Number(valor) : 0;
    }

    private formatarNumero(valor: number | null | undefined): string {
        return this.numeroSeguro(valor).toLocaleString('pt-BR');
    }

    private obterCorPrimaria(): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#3b82f6';
    }

    private obterCorTextoGrafico(): string {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        return isDark ? '#f8fafc' : '#334155';
    }

    private obterCorGridGrafico(): string {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        return isDark ? '#334155' : '#e2e8f0';
    }

    private gerarCoresMonocromaticas(corBase: string, totalItens: number): string[] {
        const { r, g, b } = this.obterRgb(corBase);
        const cores: string[] = [];

        for (let i = 0; i < totalItens; i++) {
            const opacity = 1 - i * 0.15 - (i === 0 ? 0 : 0.05);
            const cleanOpacity = Math.max(0.2, opacity);
            cores.push(`rgba(${r}, ${g}, ${b}, ${cleanOpacity})`);
        }

        return cores;
    }

    private obterRgb(cor: string): { r: number; g: number; b: number } {
        const hex = cor.trim();
        const hexMatch = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
        if (hexMatch) {
            return {
                r: parseInt(hexMatch[1], 16),
                g: parseInt(hexMatch[2], 16),
                b: parseInt(hexMatch[3], 16)
            };
        }

        const rgbMatch = hex.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
            return {
                r: Number(rgbMatch[1]),
                g: Number(rgbMatch[2]),
                b: Number(rgbMatch[3])
            };
        }

        return { r: 59, g: 130, b: 246 };
    }

    private formatarDataInput(data: Date): string {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    private formatarDataCurta(valor: string): string {
        const [ano, mes, dia] = valor.split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : '-';
    }
}
